/* Source: /home/sidsolaiyappan/skooch_ai/shared/scheduler_client.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { energyShapeCurve, energyDeficitAt, energyLevelAt, energyCurveMax, focusForTask, isEnergyV2Enabled, ENERGY_BASELINE } from "./energy_curve.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function _rangeList(n) {
  let out = [];
  let i = 0;
  while ((i < n)) {
    out = out.concat([i]);
    i = (i + 1);
  }
  return out;
}
function _floordiv(a, b) {
  return Math.floor((a / b));
}
function _trunc(x) {
  return Math.trunc(x);
}
function _pyround(x) {
  let _fl = Math.floor(x);
  let _diff = (x - _fl);
  if ((_diff < 0.5)) {
    return _fl;
  }
  if ((_diff > 0.5)) {
    return (_fl + 1);
  }
  if ((_jac.float.mod(_fl, 2) === 0)) {
    return _fl;
  }
  return (_fl + 1);
}
function _pad2(n) {
  return ((n >= 10) ? String(n) : ("0" + String(n)));
}
function _isDigits(s) {
  if ((!s || (s.length === 0))) {
    return false;
  }
  let _i = 0;
  while ((_i < s.length)) {
    let _c = s.charCodeAt(_i);
    if (((_c < 48) || (_c > 57))) {
      return false;
    }
    _i = (_i + 1);
  }
  return true;
}
function _todayDate() {
  let _n = Reflect.construct(Date, []);
  return Reflect.construct(Date, [_n.getFullYear(), _n.getMonth(), _n.getDate()]);
}
function _isoWeekday(d) {
  let _w = d.getDay();
  return ((_w === 0) ? 7 : _w);
}
function _fromIso(s) {
  if (!s) {
    return null;
  }
  let _str = String(s);
  let _parts = _str.split("-");
  if ((_parts.length !== 3)) {
    return null;
  }
  if (((!_isDigits(_parts[0]) || !_isDigits(_parts[1])) || !_isDigits(_parts[2].slice(0, 2)))) {
    return null;
  }
  let _y = Number(_parts[0]);
  let _mo = Number(_parts[1]);
  let _dStr = _parts[2].slice(0, 2);
  let _d = Number(_dStr);
  if (((((_mo < 1) || (_mo > 12)) || (_d < 1)) || (_d > 31))) {
    return null;
  }
  let _dt = Reflect.construct(Date, [_y, (_mo - 1), _d]);
  if ((((_dt.getFullYear() !== _y) || (_dt.getMonth() !== (_mo - 1))) || (_dt.getDate() !== _d))) {
    return null;
  }
  return _dt;
}
function _dayDiff(a, b) {
  let _ms = (b.getTime() - a.getTime());
  return Math.round((_ms / 86400000));
}
function _addDays(d, n) {
  return Reflect.construct(Date, [d.getFullYear(), d.getMonth(), (d.getDate() + n)]);
}
function _isoStr(d) {
  return ((((String(d.getFullYear()) + "-") + _pad2((d.getMonth() + 1))) + "-") + _pad2(d.getDate()));
}
function _parseHhmm(value) {
  if (!value) {
    return (-1);
  }
  let _parts = String(value).split(":");
  if ((_parts.length !== 2)) {
    return (-1);
  }
  let _hh = _parts[0];
  let _mm = _parts[1];
  if ((!_isDigits(_hh) || !_isDigits(_mm))) {
    return (-1);
  }
  let _h = Number(_hh);
  let _m = Number(_mm);
  if (((((_h < 0) || (_h > 23)) || (_m < 0)) || (_m > 59))) {
    return (-1);
  }
  return ((_h * 60) + _m);
}
function _deadlineDaysRemaining(deadline, referenceDay) {
  if (!deadline) {
    return (-1);
  }
  let _dd = _fromIso(deadline);
  if ((_dd === null)) {
    return (-1);
  }
  return _dayDiff(referenceDay, _dd);
}
function DEADLINE_URGENCY_BASE() {
  return 100000;
}
function TIER_ENERGY_NEUTRAL() {
  return 50;
}
function _effectiveDeadlineDays(deadline, referenceDay) {
  if (!deadline) {
    return (-99);
  }
  let _dd = _fromIso(deadline);
  if ((_dd === null)) {
    return (-99);
  }
  return _dayDiff(referenceDay, _addDays(_dd, (-1)));
}
function _candidateDate(baseDay, weekdayNum) {
  let _delta = (weekdayNum - _isoWeekday(baseDay));
  if ((_delta < 0)) {
    _delta = (_delta + 7);
  }
  return _addDays(baseDay, _delta);
}
function _dayName(dayNum) {
  let _names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  if (((dayNum >= 1) && (dayNum <= 7))) {
    return _names.at((dayNum - 1));
  }
  return ("Day " + String(dayNum));
}
function _chunkDurations(totalMins, blockLength) {
  let _maxChunk = ((blockLength && (blockLength > 0)) ? blockLength : totalMins);
  if ((_maxChunk < 30)) {
    _maxChunk = 30;
  }
  if ((totalMins <= _maxChunk)) {
    return [totalMins];
  }
  let _chunkCount = _floordiv(((totalMins + _maxChunk) - 1), _maxChunk);
  let _baseChunk = _floordiv(totalMins, _chunkCount);
  let _remainder = _jac.int.mod(totalMins, _chunkCount);
  let _chunks = [];
  for (const _idx of _rangeList(_chunkCount)) {
    let _extra = ((_idx < _remainder) ? 1 : 0);
    _chunks = _chunks.concat([(_baseChunk + _extra)]);
  }
  return _chunks;
}
function _slotToBucket(slot) {
  let _m = ((7 * 60) + (slot * 15));
  if ((_m < 720)) {
    return "morning";
  }
  if ((_m < 840)) {
    return "midday";
  }
  if ((_m < 1020)) {
    return "afternoon";
  }
  return "evening";
}
function _swapFlowAffScore(gridCat, dayIdx, slot, need, cat, cog, learnedAff, afterDeepWork, wFlow, occStart, occEnd, partnerCat, partnerStart, partnerEnd) {
  let _score = 0;
  let _flowNorm = 0;
  if ((slot > 0)) {
    let _prc = "";
    if ((((partnerCat && (partnerCat !== "")) && ((slot - 1) >= partnerStart)) && ((slot - 1) < partnerEnd))) {
      _prc = partnerCat;
    } else if ((((slot - 1) >= occStart) && ((slot - 1) < occEnd))) {
      _prc = "";
    } else {
      _prc = _jac.poly.getitem(_jac.poly.getitem(gridCat, dayIdx), (slot - 1));
    }
    if ((_prc && (_prc !== ""))) {
      let _pcw = (_jac.poly.contains(cog, _prc) ? _jac.poly.getitem(cog, _prc) : 50);
      let _ccw = (_jac.poly.contains(cog, cat) ? _jac.poly.getitem(cog, cat) : 50);
      if ((_pcw > 60)) {
        if (((afterDeepWork === "more") && (_ccw > 60))) {
          _flowNorm = 100;
        } else if (((afterDeepWork === "break") && (_ccw <= 60))) {
          _flowNorm = 100;
        } else if (((afterDeepWork === "more") && (_ccw <= 60))) {
          _flowNorm = (-60);
        } else if (((afterDeepWork === "break") && (_ccw > 60))) {
          _flowNorm = (-60);
        }
      }
    }
  }
  _score = (_score + (wFlow * _flowNorm));
  if ((learnedAff && _jac.poly.contains(learnedAff, cat))) {
    if ((_slotToBucket(slot) === _jac.poly.getitem(learnedAff, cat))) {
      _score = (_score + (12 * 100));
    }
  }
  return _score;
}
function _gapSideMatches(side, taskId, title, category) {
  if (!side) {
    return false;
  }
  let _matchedAny = false;
  if (_jac.poly.contains(side, "category")) {
    if ((String(side["category"]) !== String(category))) {
      return false;
    }
    _matchedAny = true;
  }
  if (_jac.poly.contains(side, "task_id")) {
    if ((String(side["task_id"]) !== String(taskId))) {
      return false;
    }
    _matchedAny = true;
  }
  if (_jac.poly.contains(side, "title")) {
    if ((String(side["title"]) !== String(title))) {
      return false;
    }
    _matchedAny = true;
  }
  return _matchedAny;
}
function _clearGapMins(candStart, candEnd, pStart, pEnd, dayDelta) {
  let _dd = ((dayDelta >= 0) ? dayDelta : (-dayDelta));
  if ((_dd === 0)) {
    if ((candStart >= pEnd)) {
      return (candStart - pEnd);
    }
    if ((pStart >= candEnd)) {
      return (pStart - candEnd);
    }
    return 0;
  }
  if ((dayDelta > 0)) {
    return (((1440 - pEnd) + ((_dd - 1) * 1440)) + candStart);
  }
  return (((1440 - candEnd) + ((_dd - 1) * 1440)) + pStart);
}
function _sameDayDepFloor(taskId, taskDepsById, placements, day) {
  let _deps = (_jac.poly.contains(taskDepsById, String(taskId)) ? _jac.poly.getitem(taskDepsById, String(taskId)) : []);
  if ((!_deps || (_deps.length === 0))) {
    return (-1);
  }
  let _floor = (-1);
  for (const _pl of placements) {
    if ((_pl.day !== day)) {
      continue;
    }
    let _match = false;
    for (const _depId of _deps) {
      if ((String(_pl.task_id) === String(_depId))) {
        _match = true;
      }
    }
    if (!_match) {
      continue;
    }
    let _endM = _parseHhmm(_pl.end_time);
    if ((_endM < 0)) {
      continue;
    }
    let _endSlot = _floordiv((_endM - 420), 15);
    if ((_endSlot > _floor)) {
      _floor = _endSlot;
    }
  }
  return _floor;
}
function _swapGapOk(taskId, title, category, newStartM, newEndM, newDay, partnerId, gapConstraints, placements) {
  if ((!gapConstraints || (gapConstraints.length === 0))) {
    return true;
  }
  let _btTitle = (((title !== undefined) && (title !== null)) ? title : "");
  let _taskGaps = [];
  let _maxGapSpan = 0;
  for (const _gc of gapConstraints) {
    let _gcType = (((_gc.type !== undefined) && (_gc.type !== null)) ? _gc.type : "");
    if ((_gcType !== "min_gap")) {
      continue;
    }
    let _gmin = (((_gc.min_gap_mins !== undefined) && (_gc.min_gap_mins !== null)) ? _gc.min_gap_mins : 0);
    if ((_gmin <= 0)) {
      continue;
    }
    let _ga = (((_gc.a_match !== undefined) && (_gc.a_match !== null)) ? _gc.a_match : {});
    let _gb = (((_gc.b_match !== undefined) && (_gc.b_match !== null)) ? _gc.b_match : {});
    let _matchA = _gapSideMatches(_ga, taskId, _btTitle, category);
    let _matchB = _gapSideMatches(_gb, taskId, _btTitle, category);
    if (_matchA) {
      _taskGaps = _taskGaps.concat([{"other": _gb, "min_gap": _gmin}]);
    }
    if (_matchB) {
      _taskGaps = _taskGaps.concat([{"other": _ga, "min_gap": _gmin}]);
    }
    if (((_matchA || _matchB) && (_gmin > _maxGapSpan))) {
      _maxGapSpan = _gmin;
    }
  }
  if ((_taskGaps.length === 0)) {
    return true;
  }
  let _gapDayRadius = _floordiv((_maxGapSpan + 1439), 1440);
  let _newDayIdx = (newDay - 1);
  for (const _tg of _taskGaps) {
    let _other = _tg["other"];
    let _need = _tg["min_gap"];
    for (const _pl of placements) {
      if ((_pl.task_id === taskId)) {
        continue;
      }
      if (((partnerId !== null) && (_pl.task_id === partnerId))) {
        continue;
      }
      if (!_gapSideMatches(_other, _pl.task_id, (((_pl.task_title !== undefined) && (_pl.task_title !== null)) ? _pl.task_title : ""), (((_pl.category !== undefined) && (_pl.category !== null)) ? _pl.category : ""))) {
        continue;
      }
      let _plDay = (_pl.day - 1);
      let _ddDays = (((_newDayIdx - _plDay) >= 0) ? (_newDayIdx - _plDay) : (_plDay - _newDayIdx));
      if ((_ddDays > _gapDayRadius)) {
        continue;
      }
      let _pStartM = _parseHhmm(_pl.start_time);
      let _pEndM = _parseHhmm(_pl.end_time);
      if (((_pStartM < 0) || (_pEndM < 0))) {
        continue;
      }
      let _cg = _clearGapMins(newStartM, newEndM, _pStartM, _pEndM, (_newDayIdx - _plDay));
      if ((_cg < _need)) {
        return false;
      }
    }
  }
  return true;
}
function _tupleKey(parts) {
  return _jac.poly.call(parts, "join", String.fromCharCode(0));
}
function _taskSortKey(t) {
  return _tupleKey([String((((t.title !== undefined) && (t.title !== null)) ? t.title : "")), _padNum((((t.duration_mins !== undefined) && (t.duration_mins !== null)) ? t.duration_mins : 0)), String((((t.category !== undefined) && (t.category !== null)) ? t.category : "")), String((((t.deadline !== undefined) && (t.deadline !== null)) ? t.deadline : ""))]);
}
function _fixedEventSortKey(ev) {
  return _tupleKey([String((((ev.day !== undefined) && (ev.day !== null)) ? ev.day : "")), String((((ev.start_time !== undefined) && (ev.start_time !== null)) ? ev.start_time : "")), String((((ev.end_time !== undefined) && (ev.end_time !== null)) ? ev.end_time : "")), String((((ev.category !== undefined) && (ev.category !== null)) ? ev.category : "")), String((((ev.title !== undefined) && (ev.title !== null)) ? ev.title : ""))]);
}
function _placementStableSortKey(pl) {
  return _tupleKey([String(pl.task_id), _padNum((((pl.chunk_index !== undefined) && (pl.chunk_index !== null)) ? pl.chunk_index : 0))]);
}
function _isSleepTaskName(name) {
  let _n = String((name ? name : "")).toLowerCase();
  if (!_n) {
    return false;
  }
  if (_n.includes("sleep")) {
    return true;
  }
  if (_n.includes("bedtime")) {
    return true;
  }
  return false;
}
function _placementDayStartSortKey(pl) {
  return _tupleKey([String(pl.day), String(pl.start_time), String(pl.task_id), _padNum((((pl.chunk_index !== undefined) && (pl.chunk_index !== null)) ? pl.chunk_index : 0))]);
}
function _placementFinalSortKey(p) {
  return _tupleKey([String(p.day), String(p.start_time)]);
}
function computeTierScores(categoryTiers) {
  let _tierBases = {"1": 100, "2": 60, "3": 25, "4": 8};
  let _tierCounters = {};
  let _scores = {};
  if (!categoryTiers) {
    return _scores;
  }
  for (const _ct of categoryTiers) {
    let _t = String(_ct.tier);
    if (!(_t in _tierCounters)) {
      _tierCounters[_t] = 0;
    }
    let _pos = _tierCounters[_t];
    let _base = ((_t in _tierBases) ? _tierBases[_t] : 25);
    let _val = (_base - (_pos * 5));
    if ((_val < 0)) {
      _val = 0;
    }
    _scores[_ct.category] = _val;
    _tierCounters[_t] = (_pos + 1);
  }
  return _scores;
}
function computeScoringWeights(workStyle, workLifeBalance) {
  let _wEnergy = 0;
  let _wPeak = 36;
  let _wDeadline = 34;
  let _wBalance = 10;
  let _wFlow = 6;
  let _wRank = 10;
  let _shift = ((workLifeBalance - 5) * 3);
  _wBalance = (_wBalance + _shift);
  _wPeak = (_wPeak - _shift);
  if ((_wBalance < 0)) {
    _wBalance = 0;
  }
  if ((_wPeak < 0)) {
    _wPeak = 0;
  }
  let _wSpread = 0;
  if ((workLifeBalance > 5)) {
    _wSpread = ((workLifeBalance - 5) * 8);
  }
  let _wDayLoad = 18;
  let _wTierEnergy = 12;
  return {"energy": _wEnergy, "peak": _wPeak, "deadline": _wDeadline, "balance": _wBalance, "flow": _wFlow, "rank": _wRank, "spread": _wSpread, "day_load": _wDayLoad, "tier_energy": _wTierEnergy};
}
function buildCategoryMultipliers(catOverrides) {
  let _mults = {"coding": 100, "writing": 100, "learning": 100, "creative": 100, "admin": 100, "social": 100, "fitness": 100, "other": 100};
  if (!catOverrides) {
    return _mults;
  }
  for (const _ov of catOverrides) {
    let _key = (_ov.category ? _ov.category : "");
    if (_key) {
      _key = _key.toLowerCase();
    }
    if (_key) {
      _mults[_key] = (((_ov.value !== undefined) && (_ov.value !== null)) ? _ov.value : 100);
    }
  }
  return _mults;
}
function explicitOverrideCategories(catOverrides) {
  let _out = [];
  if (!catOverrides) {
    return _out;
  }
  for (const _ov of catOverrides) {
    let _key = (_ov.category ? _ov.category : "");
    if (_key) {
      let _lk = _key.toLowerCase();
      if (!_out.includes(_lk)) {
        _out = _out.concat([_lk]);
      }
    }
  }
  return _out;
}
function blendLearnedDurationMults(baseMults, learned, explicit) {
  let _out = {};
  for (const _k of _jac.poly.call(Object, "keys", baseMults)) {
    _out[_k] = _jac.poly.getitem(baseMults, _k);
  }
  if (!learned) {
    return _out;
  }
  for (const _cat of _jac.poly.call(Object, "keys", learned)) {
    let _c = (_cat ? _cat.toLowerCase() : _cat);
    if (explicit.includes(_c)) {
      continue;
    }
    _out[_c] = _pyround(_jac.poly.getitem(learned, _cat));
  }
  return _out;
}
function compileProfileRules(globalRules, lockPersonalTime) {
  let _typeMap = {"no_schedule": "block_time", "category_limit": "max_daily_hours", "category_count_limit": "max_daily_count", "category_only": "no_category_day"};
  let _constraints = [];
  if (globalRules) {
    for (const _rule of globalRules) {
      let _rtype = (_rule.type ? _rule.type : "");
      if (!_rtype) {
        continue;
      }
      if ((_rtype === "min_gap")) {
        let _gmin = (((_rule.min_gap_mins !== undefined) && (_rule.min_gap_mins !== null)) ? _rule.min_gap_mins : 0);
        if ((_gmin <= 0)) {
          continue;
        }
        _constraints = _constraints.concat([{"id": (_rule.id ? _rule.id : ""), "type": "min_gap", "a_match": (((_rule.a_match !== undefined) && (_rule.a_match !== null)) ? _rule.a_match : {}), "b_match": (((_rule.b_match !== undefined) && (_rule.b_match !== null)) ? _rule.b_match : {}), "min_gap_mins": _gmin, "label": (_rule.label ? _rule.label : "")}]);
        continue;
      }
      let _mapped = ((_rtype in _typeMap) ? _typeMap[_rtype] : "");
      if (!_mapped) {
        continue;
      }
      _constraints = _constraints.concat([{"id": (_rule.id ? _rule.id : ""), "type": _mapped, "start_time": (_rule.start_time ? _rule.start_time : ""), "end_time": (_rule.end_time ? _rule.end_time : ""), "days": (_rule.days ? _rule.days : []), "category": (_rule.category ? _rule.category : ""), "max_hours": (((_rule.max_hours !== undefined) && (_rule.max_hours !== null)) ? _rule.max_hours : 0), "max_count": (((_rule.max_count !== undefined) && (_rule.max_count !== null)) ? _rule.max_count : 0), "label": (_rule.label ? _rule.label : "")}]);
    }
  }
  if (lockPersonalTime) {
    _constraints = _constraints.concat([{"id": "_lock_personal", "type": "block_time", "start_time": "20:00", "end_time": "23:00", "days": [6, 7], "category": "", "max_hours": 0, "label": "Personal time locked"}]);
  }
  return _constraints;
}
function _effectiveFocusPeak(focusPeak, learnedFocusPeakParam, focusPeakOverridden) {
  if ((learnedFocusPeakParam && !focusPeakOverridden)) {
    return learnedFocusPeakParam;
  }
  return focusPeak;
}
function generateScheduleClient(tasks, fixedEvents, constraints, focusPeak, blockLength, bufferMins, strategy, afterDeepWork, categoryMultipliers, scoringWeights, tierScores, sleepImportance, skipPastDays, learnedAffinity, learnedDays, gapConstraints, energyShape) {
  let SLOTS = 64;
  if (((energyShape === undefined) || (energyShape === null))) {
    energyShape = "";
  }
  if (!tasks) {
    tasks = [];
  }
  let _tasksFlex = [];
  for (const _tflex of tasks) {
    let _tfCat = String(((((_tflex !== null) && (_tflex.category !== undefined)) && (_tflex.category !== null)) ? _tflex.category : ""));
    let _tfTitle = String(((((_tflex !== null) && (_tflex.title !== undefined)) && (_tflex.title !== null)) ? _tflex.title : ""));
    if ((_isSleepTaskName(_tfCat) || _isSleepTaskName(_tfTitle))) {
      continue;
    }
    _tasksFlex = _tasksFlex.concat([_tflex]);
  }
  tasks = _tasksFlex;
  if (!fixedEvents) {
    fixedEvents = [];
  }
  if (!constraints) {
    constraints = [];
  }
  if (!learnedAffinity) {
    learnedAffinity = {};
  }
  if (!learnedDays) {
    learnedDays = {};
  }
  if (!gapConstraints) {
    gapConstraints = [];
  }
  tasks = _jac.builtin.sorted(tasks, _taskSortKey);
  fixedEvents = _jac.builtin.sorted(fixedEvents, _fixedEventSortKey);
  let _baseEnergy = [40, 50, 82, 95, 88, 55, 48, 52, 62, 68, 58, 38, 25, 20, 15, 10];
  let _cog = {"coding": 90, "writing": 85, "learning": 70, "creative": 60, "admin": 30, "social": 20, "fitness": 20, "other": 50};
  let _energyAffinity = {"coding": 90, "writing": 85, "learning": 70, "creative": 60, "admin": 30, "social": 20, "fitness": 80, "other": 50};
  let _defaultMults = {"coding": 100, "writing": 100, "learning": 100, "creative": 100, "admin": 100, "social": 100, "fitness": 100, "other": 100};
  let _mults = {};
  for (const _k of _jac.poly.call(Object, "keys", _defaultMults)) {
    _mults[_k] = _defaultMults[_k];
  }
  if ((categoryMultipliers && (_dictLen(categoryMultipliers) > 0))) {
    for (const _k of _jac.poly.call(Object, "keys", categoryMultipliers)) {
      _mults[_k] = _jac.poly.getitem(categoryMultipliers, _k);
    }
  }
  let _shift = 0;
  if ((focusPeak === "midday")) {
    _shift = 2;
  } else if ((focusPeak === "afternoon")) {
    _shift = 4;
  } else if ((focusPeak === "evening")) {
    _shift = 6;
  }
  let _energy = [];
  for (const _ei of _rangeList(16)) {
    let _si = ((_ei - _shift) % 16);
    if ((_si < 0)) {
      _si = (_si + 16);
    }
    let _val = _baseEnergy.at(_si);
    if (((sleepImportance > 5) && (_ei >= 12))) {
      let _sleepPenalty = (((sleepImportance - 5) * 4) * (_ei - 11));
      _val = (_val - _sleepPenalty);
      if ((_val < 5)) {
        _val = 5;
      }
    }
    if (((sleepImportance > 5) && (_ei < 2))) {
      let _sleepEarlyPen = (((sleepImportance - 5) * 4) * (2 - _ei));
      _val = (_val - _sleepEarlyPen);
      if ((_val < 5)) {
        _val = 5;
      }
    }
    _energy = _energy.concat([_val]);
  }
  let _peakS = 4;
  let _peakE = 20;
  if ((focusPeak === "midday")) {
    _peakS = 16;
    _peakE = 28;
  } else if ((focusPeak === "afternoon")) {
    _peakS = 24;
    _peakE = 40;
  } else if ((focusPeak === "evening")) {
    _peakS = 44;
    _peakE = 60;
  }
  let _energyV2On = false;
  if (((isEnergyV2Enabled() && energyShape) && (energyShape !== ""))) {
    _energyV2On = true;
  }
  let _eCurve = [];
  let _peakMax = 0;
  let _plateauAbove = 1;
  if (_energyV2On) {
    _eCurve = energyShapeCurve(energyShape);
    _peakMax = energyCurveMax(_eCurve);
    _plateauAbove = ((_peakMax - 8) - ENERGY_BASELINE());
    if ((_plateauAbove < 1)) {
      _plateauAbove = 1;
    }
  }
  let _W_ENERGY_NUM = 1;
  let _W_ENERGY_DEN = 20;
  let _SLEEP_EARLY_EDGE = 8;
  let _SLEEP_LATE_EDGE = 48;
  let _sleepEdgeWeight = 0;
  if ((sleepImportance > 5)) {
    _sleepEdgeWeight = (sleepImportance - 5);
  }
  let _wEnergy = 0;
  let _wPeak = 36;
  let _wDeadline = 34;
  let _wBalance = 10;
  let _wFlow = 6;
  let _wRank = 10;
  let _wSpread = 0;
  let _wDayLoad = 18;
  let _wTierEnergy = 12;
  if ((scoringWeights && (_dictLen(scoringWeights) > 0))) {
    _wEnergy = (((scoringWeights.energy !== undefined) && (scoringWeights.energy !== null)) ? scoringWeights.energy : 0);
    _wPeak = (((scoringWeights.peak !== undefined) && (scoringWeights.peak !== null)) ? scoringWeights.peak : 36);
    _wDeadline = (((scoringWeights.deadline !== undefined) && (scoringWeights.deadline !== null)) ? scoringWeights.deadline : 34);
    _wBalance = (((scoringWeights.balance !== undefined) && (scoringWeights.balance !== null)) ? scoringWeights.balance : 10);
    _wFlow = (((scoringWeights.flow !== undefined) && (scoringWeights.flow !== null)) ? scoringWeights.flow : 6);
    _wRank = (((scoringWeights.rank !== undefined) && (scoringWeights.rank !== null)) ? scoringWeights.rank : 10);
    _wSpread = (((scoringWeights.spread !== undefined) && (scoringWeights.spread !== null)) ? scoringWeights.spread : 0);
    _wDayLoad = (((scoringWeights.day_load !== undefined) && (scoringWeights.day_load !== null)) ? scoringWeights.day_load : 18);
    _wTierEnergy = (((scoringWeights.tier_energy !== undefined) && (scoringWeights.tier_energy !== null)) ? scoringWeights.tier_energy : 12);
  }
  let _grid = [];
  let _gridCat = [];
  for (const _d of _rangeList(7)) {
    let _row = [];
    let _crow = [];
    for (const _s of _rangeList(SLOTS)) {
      _row = _row.concat([0]);
      _crow = _crow.concat([""]);
    }
    _grid = _grid.concat([_row]);
    _gridCat = _gridCat.concat([_crow]);
  }
  let _alldayDays = [false, false, false, false, false, false, false];
  for (const _ev of fixedEvents) {
    if (_ev.excluded_from_optimizer) {
      continue;
    }
    if (((_ev.day === undefined) || (_ev.day === null))) {
      continue;
    }
    let _di = (_ev.day - 1);
    if (((_di < 0) || (_di > 6))) {
      continue;
    }
    let _startMins = _parseHhmm(_ev.start_time);
    let _endMins = _parseHhmm(_ev.end_time);
    if ((((_startMins < 0) || (_endMins < 0)) || (_endMins <= _startMins))) {
      continue;
    }
    let _sh = _floordiv(_startMins, 60);
    let _sm = _jac.int.mod(_startMins, 60);
    let _eh = _floordiv(_endMins, 60);
    let _em = _jac.int.mod(_endMins, 60);
    let _ss = (((_sh - 7) * 4) + _floordiv(_sm, 15));
    let _es = (((_eh - 7) * 4) + _floordiv((_em + 14), 15));
    let _loS = ((_ss < 0) ? 0 : _ss);
    let _hiS = ((_es > SLOTS) ? SLOTS : _es);
    for (const _s of _rangeList(SLOTS)) {
      if (((_s >= _loS) && (_s < _hiS))) {
        _grid.at(_di)[_s] = (-1);
        _gridCat.at(_di)[_s] = (((_ev.category !== undefined) && (_ev.category !== null)) ? _ev.category : "other");
      }
    }
    let _evIsAllday = (Boolean(_ev.is_all_day) || ((_startMins === 420) && (_endMins >= 1380)));
    if (_evIsAllday) {
      _jac.poly.setitem(_alldayDays, _di, true);
    }
  }
  let _fixedMinsPerDay = [0, 0, 0, 0, 0, 0, 0];
  for (const _fd of _rangeList(7)) {
    let _fm = 0;
    for (const _fs of _rangeList(SLOTS)) {
      if ((_jac.poly.getitem(_grid.at(_fd), _fs) === (-1))) {
        _fm = (_fm + 15);
      }
    }
    _jac.poly.setitem(_fixedMinsPerDay, _fd, _fm);
  }
  let _eventDayById = {};
  for (const _evd of fixedEvents) {
    let _evid = (((_evd.id !== undefined) && (_evd.id !== null)) ? _evd.id : "");
    if (!_evid) {
      continue;
    }
    if (((_evd.day === undefined) || (_evd.day === null))) {
      continue;
    }
    let _evdDay = _evd.day;
    if (((_evdDay < 1) || (_evdDay > 7))) {
      continue;
    }
    _eventDayById[String(_evid)] = _evdDay;
  }
  let _blockedCats = {};
  let _dailyMaxMins = [960, 960, 960, 960, 960, 960, 960];
  let _catCountCaps = {};
  let _dayCatCount = {};
  for (const _con of constraints) {
    let _ctype = _con.type;
    if ((_ctype === "block_time")) {
      let _startMins = _parseHhmm(_con.start_time);
      let _endMins = _parseHhmm(_con.end_time);
      if ((((_startMins < 0) || (_endMins < 0)) || (_endMins <= _startMins))) {
        continue;
      }
      let _ss = (((_floordiv(_startMins, 60) - 7) * 4) + _floordiv(_jac.int.mod(_startMins, 60), 15));
      let _es = (((_floordiv(_endMins, 60) - 7) * 4) + _floordiv((_jac.int.mod(_endMins, 60) + 14), 15));
      let _loS = ((_ss < 0) ? 0 : _ss);
      let _hiS = ((_es > SLOTS) ? SLOTS : _es);
      let _bDays = (_con.days ? _con.days : []);
      if ((_bDays && (_bDays.length > 0))) {
        for (const _dayNum of _bDays) {
          let _di = (_dayNum - 1);
          if (((_di < 0) || (_di > 6))) {
            continue;
          }
          for (const _s of _rangeList(SLOTS)) {
            if (((_s >= _loS) && (_s < _hiS))) {
              _grid.at(_di)[_s] = (-2);
            }
          }
        }
      } else {
        for (const _di of _rangeList(7)) {
          for (const _s of _rangeList(SLOTS)) {
            if (((_s >= _loS) && (_s < _hiS))) {
              _grid.at(_di)[_s] = (-2);
            }
          }
        }
      }
    } else if ((_ctype === "no_category_day")) {
      let _ncDays = (_con.days ? _con.days : []);
      if ((_ncDays && (_ncDays.length > 0))) {
        for (const _dayNum of _ncDays) {
          let _di = (_dayNum - 1);
          if (!(String(_di) in _blockedCats)) {
            _blockedCats[String(_di)] = [];
          }
          _blockedCats[String(_di)] = _blockedCats[String(_di)].concat([_con.category]);
        }
      } else {
        for (const _di of _rangeList(7)) {
          if (!(String(_di) in _blockedCats)) {
            _blockedCats[String(_di)] = [];
          }
          _blockedCats[String(_di)] = _blockedCats[String(_di)].concat([_con.category]);
        }
      }
    } else if ((_ctype === "max_daily_hours")) {
      let _cap = ((((_con.max_hours !== undefined) && (_con.max_hours !== null)) ? _con.max_hours : 16) * 60);
      if ((_cap <= 0)) {
        continue;
      }
      let _cDays = (_con.days ? _con.days : []);
      if ((_cDays && (_cDays.length > 0))) {
        for (const _dayNum of _cDays) {
          let _di = (_dayNum - 1);
          if (((_di < 0) || (_di > 6))) {
            continue;
          }
          if ((_cap < _dailyMaxMins.at(_di))) {
            _jac.poly.setitem(_dailyMaxMins, _di, _cap);
          }
        }
      } else {
        for (const _di of _rangeList(7)) {
          if ((_cap < _dailyMaxMins.at(_di))) {
            _jac.poly.setitem(_dailyMaxMins, _di, _cap);
          }
        }
      }
    } else if ((_ctype === "max_daily_count")) {
      let _ccap = (((_con.max_count !== undefined) && (_con.max_count !== null)) ? _con.max_count : 0);
      let _ccat = (((_con.category !== undefined) && (_con.category !== null)) ? _con.category : "");
      if (((_ccap <= 0) || !_ccat)) {
        continue;
      }
      let _ccDays = (_con.days ? _con.days : []);
      if ((!_ccDays || (_ccDays.length === 0))) {
        _ccDays = [1, 2, 3, 4, 5, 6, 7];
      }
      for (const _dayNum of _ccDays) {
        let _di = (_dayNum - 1);
        if (((_di < 0) || (_di > 6))) {
          continue;
        }
        let _dk = String(_di);
        if (!(_dk in _catCountCaps)) {
          _catCountCaps[_dk] = {};
        }
        if ((!_jac.poly.contains(_catCountCaps[_dk], _ccat) || (_ccap < _jac.poly.getitem(_catCountCaps[_dk], _ccat)))) {
          _catCountCaps[_dk][_ccat] = _ccap;
        }
      }
    }
  }
  let _bufSlots = _floordiv(bufferMins, 15);
  if ((_jac.int.mod(bufferMins, 15) > 0)) {
    _bufSlots = (_bufSlots + 1);
  }
  let _placedIds = [];
  let _placements = [];
  let _overflowList = [];
  let _today = _todayDate();
  let _nowDt = Reflect.construct(Date, []);
  let _todayWeekday = _isoWeekday(_today);
  let _earliestTodaySlot = _floordiv(((((_nowDt.getHours() * 60) + _nowDt.getMinutes()) - (7 * 60)) + 14), 15);
  if ((_earliestTodaySlot < 0)) {
    _earliestTodaySlot = 0;
  }
  if ((_earliestTodaySlot > SLOTS)) {
    _earliestTodaySlot = SLOTS;
  }
  let _totalFlexMins = 0;
  for (const _st of tasks) {
    let _scat = (((_st.category !== undefined) && (_st.category !== null)) ? _st.category : "other");
    let _smult = ((_scat in _mults) ? _mults[_scat] : 100);
    _totalFlexMins = (_totalFlexMins + _trunc(_floordiv((_st.duration_mins * _smult), 100)));
  }
  let _activeDays = 7;
  if (skipPastDays) {
    _activeDays = 0;
    for (const _ad of _rangeList(7)) {
      if (((_ad + 1) >= _todayWeekday)) {
        _activeDays = (_activeDays + 1);
      }
    }
  }
  let _targetPerDay = (_totalFlexMins / Math.max(_activeDays, 1));
  for (const _roundNum of _rangeList(tasks.length)) {
    let _bestTask = null;
    let _bestUrgency = (-9999);
    for (const _t of tasks) {
      let _tid = _t.id;
      let _already = false;
      for (const _pid of _placedIds) {
        if ((_pid === _tid)) {
          _already = true;
        }
      }
      if (_already) {
        continue;
      }
      let _depsReady = true;
      let _tDeps = (((_t.depends_on !== undefined) && (_t.depends_on !== null)) ? _t.depends_on : []);
      if ((_tDeps && (_tDeps.length > 0))) {
        for (const _depId of _tDeps) {
          let _depPlaced = false;
          for (const _pidD of _placedIds) {
            if ((String(_pidD) === String(_depId))) {
              _depPlaced = true;
            }
          }
          if (!_depPlaced) {
            _depsReady = false;
            break;
          }
        }
      }
      if (!_depsReady) {
        continue;
      }
      let _urgency = 0;
      let _dl = (_t.deadline ? _t.deadline : "");
      let _tEvId = (((_t.before_event_id !== undefined) && (_t.before_event_id !== null)) ? _t.before_event_id : "");
      if ((_tEvId && (String(_tEvId) in _eventDayById))) {
        let _tEvDate = _candidateDate(_today, _eventDayById[String(_tEvId)]);
        let _tEvStr = _isoStr(_tEvDate);
        if (!_dl) {
          _dl = _tEvStr;
        } else {
          let _tExist = _fromIso(_dl);
          if (((_tExist === null) || (_dayDiff(_tExist, _tEvDate) < 0))) {
            _dl = _tEvStr;
          }
        }
      }
      let _hasParsedDeadline = false;
      let _dlRem = 0;
      if (_dl) {
        let _parsed = (_fromIso(_dl) !== null);
        if (_parsed) {
          _hasParsedDeadline = true;
          _dlRem = _deadlineDaysRemaining(_dl, _today);
        }
      }
      if (_hasParsedDeadline) {
        if ((_dlRem < 0)) {
          _urgency = ((DEADLINE_URGENCY_BASE() + 1500) + _t.priority);
        } else {
          _urgency = ((DEADLINE_URGENCY_BASE() + (1000 - _dlRem)) + _t.priority);
        }
      } else if ((strategy === "deadlines")) {
        _urgency = _t.priority;
      } else if ((strategy === "priority")) {
        _urgency = (_t.priority * 100);
      } else {
        let _adj = _t.duration_mins;
        _urgency = (1000 - _adj);
      }
      if ((_urgency > _bestUrgency)) {
        _bestUrgency = _urgency;
        _bestTask = _t;
      }
    }
    if ((_bestTask === null)) {
      for (const _tb of tasks) {
        let _tbId = _tb.id;
        let _tbAlready = false;
        for (const _pidB of _placedIds) {
          if ((_pidB === _tbId)) {
            _tbAlready = true;
          }
        }
        if (!_tbAlready) {
          _bestTask = _tb;
          break;
        }
      }
    }
    if ((_bestTask === null)) {
      break;
    }
    let _cat = (((_bestTask.category !== undefined) && (_bestTask.category !== null)) ? _bestTask.category : "other");
    let _catMult = ((_cat in _mults) ? _mults[_cat] : 100);
    let _adjMins = _trunc(_floordiv((_bestTask.duration_mins * _catMult), 100));
    let _chunkList = _chunkDurations(_adjMins, blockLength);
    let _btFocus = 0;
    if (_energyV2On) {
      _btFocus = focusForTask(_bestTask);
    }
    let _taskGaps = [];
    let _maxGapSpan = 0;
    let _btTitle = (((_bestTask.title !== undefined) && (_bestTask.title !== null)) ? _bestTask.title : "");
    for (const _gc of gapConstraints) {
      let _gcType = (((_gc.type !== undefined) && (_gc.type !== null)) ? _gc.type : "");
      if ((_gcType !== "min_gap")) {
        continue;
      }
      let _gmin = (((_gc.min_gap_mins !== undefined) && (_gc.min_gap_mins !== null)) ? _gc.min_gap_mins : 0);
      if ((_gmin <= 0)) {
        continue;
      }
      let _ga = (((_gc.a_match !== undefined) && (_gc.a_match !== null)) ? _gc.a_match : {});
      let _gb = (((_gc.b_match !== undefined) && (_gc.b_match !== null)) ? _gc.b_match : {});
      let _matchA = _gapSideMatches(_ga, _bestTask.id, _btTitle, _cat);
      let _matchB = _gapSideMatches(_gb, _bestTask.id, _btTitle, _cat);
      if (_matchA) {
        _taskGaps = _taskGaps.concat([{"other": _gb, "min_gap": _gmin}]);
      }
      if (_matchB) {
        _taskGaps = _taskGaps.concat([{"other": _ga, "min_gap": _gmin}]);
      }
      if (((_matchA || _matchB) && (_gmin > _maxGapSpan))) {
        _maxGapSpan = _gmin;
      }
    }
    let _gapDayRadius = _floordiv((_maxGapSpan + 1439), 1440);
    let _remainingMins = _adjMins;
    let _failedChunkMins = 0;
    let _blockedDays = [];
    let _capBlockedDays = [];
    let _countBlockedDays = [];
    let _countedDays = [];
    let _deadlineBlockedDays = [];
    let _depBlockedDays = [];
    let _windowBlockedDays = [];
    let _candidateSlots = 0;
    let _depEarliestDay = (-1);
    let _depEarliestSlot = (-1);
    let _btDeps = (((_bestTask.depends_on !== undefined) && (_bestTask.depends_on !== null)) ? _bestTask.depends_on : []);
    if ((_btDeps && (_btDeps.length > 0))) {
      for (const _dpl of _placements) {
        let _dplMatch = false;
        for (const _depId of _btDeps) {
          if ((String(_dpl.task_id) === String(_depId))) {
            _dplMatch = true;
          }
        }
        if (!_dplMatch) {
          continue;
        }
        let _dplDay = (_dpl.day - 1);
        let _dplEndM = _parseHhmm(_dpl.end_time);
        if ((_dplEndM < 0)) {
          continue;
        }
        let _dplEndSlot = _floordiv((_dplEndM - 420), 15);
        if (((_dplDay > _depEarliestDay) || ((_dplDay === _depEarliestDay) && (_dplEndSlot > _depEarliestSlot)))) {
          _depEarliestDay = _dplDay;
          _depEarliestSlot = _dplEndSlot;
        }
      }
    }
    let _btDeadline = (((_bestTask.deadline !== undefined) && (_bestTask.deadline !== null)) ? _bestTask.deadline : "");
    let _btEventId = (((_bestTask.before_event_id !== undefined) && (_bestTask.before_event_id !== null)) ? _bestTask.before_event_id : "");
    if ((_btEventId && (String(_btEventId) in _eventDayById))) {
      let _evGridDay = _eventDayById[String(_btEventId)];
      let _evDate = _candidateDate(_today, _evGridDay);
      let _evDueStr = _isoStr(_evDate);
      if (!_btDeadline) {
        _btDeadline = _evDueStr;
      } else {
        let _existingDate = _fromIso(_btDeadline);
        if (((_existingDate === null) || (_dayDiff(_existingDate, _evDate) < 0))) {
          _btDeadline = _evDueStr;
        }
      }
    }
    let _winSlotStart = (-1);
    let _winSlotEnd = (-1);
    let _btWindow = (((_bestTask.time_window !== undefined) && (_bestTask.time_window !== null)) ? _bestTask.time_window : null);
    if ((_btWindow && (_btWindow.length >= 2))) {
      let _winStartM = _btWindow[0];
      let _winEndM = _btWindow[1];
      if ((((((_winStartM !== undefined) && (_winStartM !== null)) && (_winEndM !== undefined)) && (_winEndM !== null)) && (_winEndM > _winStartM))) {
        let _wsRel = (_winStartM - 420);
        _winSlotStart = _floordiv((_wsRel + 14), 15);
        if ((_winSlotStart < 0)) {
          _winSlotStart = 0;
        }
        _winSlotEnd = _floordiv((_winEndM - 420), 15);
        if ((_winSlotEnd > SLOTS)) {
          _winSlotEnd = SLOTS;
        }
        if ((_winSlotEnd < 0)) {
          _winSlotEnd = 0;
        }
      }
    }
    let _prefDay = (-1);
    let _prefDateStr = (_bestTask.preferred_date ? _bestTask.preferred_date : "");
    if ((_prefDateStr && (String(_prefDateStr).length >= 10))) {
      let _pd = _fromIso(String(_prefDateStr));
      if ((_pd !== null)) {
        _prefDay = _isoWeekday(_pd);
      } else {
        _prefDay = (-1);
      }
    }
    let _prefSlotStart = (-1);
    let _prefSlotEnd = (-1);
    let _prefTimeStr = String((_bestTask.preferred_time ? _bestTask.preferred_time : ""));
    if (!_prefTimeStr) {
      let _titleLower = ((_bestTask.title ? _bestTask.title : "")).toLowerCase();
      if (((_titleLower.includes("dinner") || _titleLower.includes("cook")) || _titleLower.includes("supper"))) {
        _prefTimeStr = "evening";
      } else if (_titleLower.includes("breakfast")) {
        _prefTimeStr = "morning";
      } else if (_titleLower.includes("lunch")) {
        _prefTimeStr = "afternoon";
      }
    }
    if (_prefTimeStr) {
      let _ptl = _prefTimeStr.toLowerCase().trim();
      if ((_ptl === "morning")) {
        _prefSlotStart = 0;
        _prefSlotEnd = 20;
      } else if ((_ptl === "afternoon")) {
        _prefSlotStart = 20;
        _prefSlotEnd = 40;
      } else if ((_ptl === "evening")) {
        _prefSlotStart = 40;
        _prefSlotEnd = 56;
      } else if (_ptl.includes(":")) {
        let _ptParts = _jac.poly.call(_ptl, "split", ":");
        if ((((_ptParts.length >= 2) && _isDigits(_ptParts[0])) && _isDigits(_ptParts[1]))) {
          let _ptH = (Number(_ptParts[0]) - 7);
          let _ptM = Number(_ptParts[1]);
          let _ptCenter = ((_ptH * 4) + _floordiv(_ptM, 15));
          _prefSlotStart = (_ptCenter - 4);
          _prefSlotEnd = (_ptCenter + 4);
          if ((_prefSlotStart < 0)) {
            _prefSlotStart = 0;
          }
          if ((_prefSlotEnd > SLOTS)) {
            _prefSlotEnd = SLOTS;
          }
        } else {
          _prefSlotStart = (-1);
          _prefSlotEnd = (-1);
        }
      }
    }
    for (const _chunkIdx of _rangeList(_chunkList.length)) {
      let _chunkMins = _chunkList.at(_chunkIdx);
      _failedChunkMins = _chunkMins;
      let _slotsNeeded = _floordiv((_chunkMins + 14), 15);
      let _bestScore = (-1);
      let _bestDay = (-1);
      let _bestSlot = (-1);
      let _bestDeadlineRank = (-1);
      let _secondBestScore = (-1);
      let _secondBestDay = (-1);
      let _secondBestSlot = (-1);
      let _secondBestDeadlineRank = (-1);
      let _chunkBlockedDays = [];
      let _chunkCapBlockedDays = [];
      let _chunkCountBlockedDays = [];
      let _chunkDeadlineBlockedDays = [];
      let _chunkDepBlockedDays = [];
      let _chunkWindowBlockedDays = [];
      let _chunkCandidateSlots = 0;
      for (const _day of _rangeList(7)) {
        if ((skipPastDays && ((_day + 1) < _todayWeekday))) {
          continue;
        }
        if (((_prefDay > 0) && ((_day + 1) !== _prefDay))) {
          continue;
        }
        if (((_depEarliestDay >= 0) && (_day < _depEarliestDay))) {
          if (!_chunkDepBlockedDays.includes((_day + 1))) {
            _chunkDepBlockedDays = _chunkDepBlockedDays.concat([(_day + 1)]);
          }
          continue;
        }
        if ((String(_day) in _blockedCats)) {
          let _blocked = false;
          for (const _bc of _blockedCats[String(_day)]) {
            if ((_bc === _cat)) {
              _blocked = true;
            }
          }
          if (_blocked) {
            _chunkBlockedDays = _chunkBlockedDays.concat([(_day + 1)]);
            continue;
          }
        }
        let _dayMins = 0;
        for (const _s of _rangeList(SLOTS)) {
          if ((_jac.poly.getitem(_grid.at(_day), _s) > 0)) {
            _dayMins = (_dayMins + 15);
          }
        }
        let _dayCap = _dailyMaxMins.at(_day);
        if (((_dayMins + _chunkMins) > _dayCap)) {
          _chunkCapBlockedDays = _chunkCapBlockedDays.concat([(_day + 1)]);
          continue;
        }
        let _dkDay = String(_day);
        if (((_dkDay in _catCountCaps) && _jac.poly.contains(_catCountCaps[_dkDay], _cat))) {
          let _alreadyCountedHere = _countedDays.includes(_day);
          let _placedHere = 0;
          if (((_dkDay in _dayCatCount) && _jac.poly.contains(_dayCatCount[_dkDay], _cat))) {
            _placedHere = _jac.poly.getitem(_dayCatCount[_dkDay], _cat);
          }
          if ((!_alreadyCountedHere && (_placedHere >= _jac.poly.getitem(_catCountCaps[_dkDay], _cat)))) {
            _chunkCountBlockedDays = _chunkCountBlockedDays.concat([(_day + 1)]);
            continue;
          }
        }
        let _pinnedAllday = ((_prefDay > 0) && _alldayDays.at(_day));
        for (const _slot of _rangeList(((SLOTS - _slotsNeeded) + 1))) {
          if (((skipPastDays && ((_day + 1) === _todayWeekday)) && (_slot < _earliestTodaySlot))) {
            continue;
          }
          let _valid = true;
          for (const _s of _rangeList(_slotsNeeded)) {
            let _sIdx = (_slot + _s);
            let _sv = _jac.poly.getitem(_grid.at(_day), _sIdx);
            if (_pinnedAllday) {
              if (((_sv !== 0) && (_sv !== (-1)))) {
                _valid = false;
                break;
              }
            } else {
              if ((_sv !== 0)) {
                _valid = false;
                break;
              }
            }
          }
          if (!_valid) {
            continue;
          }
          if (!_pinnedAllday) {
            if (((_bufSlots > 0) && (_slot > 0))) {
              let _pc = _jac.poly.getitem(_gridCat.at(_day), (_slot - 1));
              if (((_pc && (_pc !== "")) && (_pc !== _cat))) {
                let _bok = true;
                let _bStart = (((_slot - _bufSlots) < 0) ? 0 : (_slot - _bufSlots));
                for (const _b of _rangeList((_slot - _bStart))) {
                  if ((_jac.poly.getitem(_grid.at(_day), (_bStart + _b)) !== 0)) {
                    _bok = false;
                    break;
                  }
                }
                if (!_bok) {
                  continue;
                }
              }
            }
            let _endS = (_slot + _slotsNeeded);
            if (((_bufSlots > 0) && (_endS < SLOTS))) {
              let _nc = _jac.poly.getitem(_gridCat.at(_day), _endS);
              if (((_nc && (_nc !== "")) && (_nc !== _cat))) {
                let _bok = true;
                let _bEnd = (((_endS + _bufSlots) > SLOTS) ? SLOTS : (_endS + _bufSlots));
                for (const _b of _rangeList((_bEnd - _endS))) {
                  if ((_jac.poly.getitem(_grid.at(_day), (_endS + _b)) !== 0)) {
                    _bok = false;
                    break;
                  }
                }
                if (!_bok) {
                  continue;
                }
              }
            }
          }
          if (_jac.builtin.bool(_jac.poly.and_(_taskGaps, () => (_taskGaps.length > 0)))) {
            let _candStartM = (420 + (_slot * 15));
            let _candEndM = (420 + ((_slot + _slotsNeeded) * 15));
            let _gapOk = true;
            for (const _tg of _taskGaps) {
              let _other = _tg["other"];
              let _need = _tg["min_gap"];
              for (const _pl of _placements) {
                if ((_pl.task_id === _bestTask.id)) {
                  continue;
                }
                if (!_gapSideMatches(_other, _pl.task_id, (((_pl.task_title !== undefined) && (_pl.task_title !== null)) ? _pl.task_title : ""), (((_pl.category !== undefined) && (_pl.category !== null)) ? _pl.category : ""))) {
                  continue;
                }
                let _plDay = (_pl.day - 1);
                let _ddDays = (((_day - _plDay) >= 0) ? (_day - _plDay) : (_plDay - _day));
                if ((_ddDays > _gapDayRadius)) {
                  continue;
                }
                let _pStartM = _parseHhmm(_pl.start_time);
                let _pEndM = _parseHhmm(_pl.end_time);
                if (((_pStartM < 0) || (_pEndM < 0))) {
                  continue;
                }
                let _cg = _clearGapMins(_candStartM, _candEndM, _pStartM, _pEndM, (_day - _plDay));
                if ((_cg < _need)) {
                  _gapOk = false;
                  break;
                }
              }
              if (!_gapOk) {
                break;
              }
            }
            if (!_gapOk) {
              continue;
            }
          }
          if (((_winSlotStart >= 0) && (_winSlotEnd > _winSlotStart))) {
            if (((_slot < _winSlotStart) || ((_slot + _slotsNeeded) > _winSlotEnd))) {
              if (!_chunkWindowBlockedDays.includes((_day + 1))) {
                _chunkWindowBlockedDays = _chunkWindowBlockedDays.concat([(_day + 1)]);
              }
              continue;
            }
          }
          if (((_depEarliestDay >= 0) && (_day === _depEarliestDay))) {
            if ((_slot < _depEarliestSlot)) {
              if (!_chunkDepBlockedDays.includes((_day + 1))) {
                _chunkDepBlockedDays = _chunkDepBlockedDays.concat([(_day + 1)]);
              }
              continue;
            }
          }
          let _deadlineNorm = 0;
          let _deadlineRank = 0;
          let _dl = (_btDeadline ? _btDeadline : "");
          if (_dl) {
            let _candidateDay = _candidateDate(_today, (_day + 1));
            let _effRem = _effectiveDeadlineDays(_dl, _candidateDay);
            let _daysRem = _deadlineDaysRemaining(_dl, _candidateDay);
            if ((_effRem !== (-99))) {
              let _deadlineFeasible = (_effRem >= 0);
              let _isToday = ((_day + 1) === _todayWeekday);
              let _effRemToday = _effectiveDeadlineDays(_dl, _today);
              if ((((!_deadlineFeasible && _isToday) && (_effRemToday < 1)) && (_daysRem >= 0))) {
                _deadlineFeasible = true;
              }
              if (!_deadlineFeasible) {
                if (!_chunkDeadlineBlockedDays.includes((_day + 1))) {
                  _chunkDeadlineBlockedDays = _chunkDeadlineBlockedDays.concat([(_day + 1)]);
                }
                continue;
              }
              _deadlineRank = (_effRem + 1);
              if ((_deadlineRank < 1)) {
                _deadlineRank = 1;
              }
              if ((_daysRem >= 0)) {
                _deadlineNorm = _floordiv(2000, ((_daysRem * 50) + 50));
                if ((_deadlineNorm > 100)) {
                  _deadlineNorm = 100;
                }
              } else {
                _deadlineNorm = 100;
              }
            }
          }
          _chunkCandidateSlots = (_chunkCandidateSlots + 1);
          let _eIdx = _floordiv(_slot, 4);
          if ((_eIdx >= 16)) {
            _eIdx = 15;
          }
          let _eLevel = _energy.at(_eIdx);
          let _cWeight = ((_cat in _cog) ? _cog[_cat] : 50);
          let _eAffinity = ((_cat in _energyAffinity) ? _energyAffinity[_cat] : _cWeight);
          let _energyNorm = _floordiv(((_eLevel * _eAffinity) * 100), 8550);
          if ((_energyNorm > 100)) {
            _energyNorm = 100;
          }
          let _peakNorm = 0;
          if (((_slot >= _peakS) && (_slot < _peakE))) {
            _peakNorm = (_bestTask.priority * 10);
            if ((_peakNorm > 100)) {
              _peakNorm = 100;
            }
          }
          let _balanceDen = ((_dayCap > 1) ? _dayCap : 1);
          let _balanceNorm = (100 - _floordiv((_dayMins * 100), _balanceDen));
          if ((_balanceNorm < 0)) {
            _balanceNorm = 0;
          }
          let _projected = (_dayMins + _chunkMins);
          let _spreadNorm = 100;
          if ((_projected > _targetPerDay)) {
            _spreadNorm = (100 - _floordiv(((_projected - _targetPerDay) * 100), Math.max(_targetPerDay, 1)));
            if ((_spreadNorm < 0)) {
              _spreadNorm = 0;
            }
          }
          let _dayCapacity = (_dayCap - _fixedMinsPerDay.at(_day));
          if ((_dayCapacity < 1)) {
            _dayCapacity = 1;
          }
          let _loadPct = _floordiv((_projected * 100), _dayCapacity);
          let _dayLoadNorm = _floordiv((_loadPct * _loadPct), 100);
          let _flowNorm = 0;
          if ((_slot > 0)) {
            let _prc = _jac.poly.getitem(_gridCat.at(_day), (_slot - 1));
            if ((_prc && (_prc !== ""))) {
              let _pcw = ((_prc in _cog) ? _cog[_prc] : 50);
              let _ccw = _cWeight;
              if ((_pcw > 60)) {
                if (((afterDeepWork === "more") && (_ccw > 60))) {
                  _flowNorm = 100;
                } else if (((afterDeepWork === "break") && (_ccw <= 60))) {
                  _flowNorm = 100;
                } else if (((afterDeepWork === "more") && (_ccw <= 60))) {
                  _flowNorm = (-60);
                } else if (((afterDeepWork === "break") && (_ccw > 60))) {
                  _flowNorm = (-60);
                }
              }
            }
          }
          let _rankNorm = 25;
          let _hasTier = false;
          if (((tierScores && (_dictLen(tierScores) > 0)) && _jac.poly.contains(tierScores, _cat))) {
            _rankNorm = _jac.poly.getitem(tierScores, _cat);
            _hasTier = true;
          }
          if ((_rankNorm > 100)) {
            _rankNorm = 100;
          }
          let _tierEnergyNorm = 0;
          if ((_energyV2On && _hasTier)) {
            let _teLvlSum = 0;
            for (const _te of _rangeList(_slotsNeeded)) {
              _teLvlSum = (_teLvlSum + energyLevelAt(_eCurve, (_slot + _te)));
            }
            let _teMeanLvl = _floordiv(_teLvlSum, ((_slotsNeeded > 0) ? _slotsNeeded : 1));
            let _teAbove = (_teMeanLvl - ENERGY_BASELINE());
            if ((_teAbove < 0)) {
              _teAbove = 0;
            }
            let _teEff = _teAbove;
            if ((_teEff > _plateauAbove)) {
              _teEff = _plateauAbove;
            }
            let _slotEnergyNorm = _floordiv((_teEff * 100), ((_plateauAbove > 0) ? _plateauAbove : 1));
            if ((_slotEnergyNorm > 100)) {
              _slotEnergyNorm = 100;
            }
            _tierEnergyNorm = _floordiv(((_rankNorm - TIER_ENERGY_NEUTRAL()) * _slotEnergyNorm), 100);
          }
          if (((_prefSlotStart >= 0) && (_prefSlotEnd > _prefSlotStart))) {
            if (((_slot < _prefSlotStart) || (_slot >= _prefSlotEnd))) {
              continue;
            }
          }
          let _affBonus = 0;
          if ((learnedAffinity && _jac.poly.contains(learnedAffinity, _cat))) {
            if ((_slotToBucket(_slot) === _jac.poly.getitem(learnedAffinity, _cat))) {
              _affBonus = 12;
            }
          }
          let _dayBonus = 0;
          if ((learnedDays && _jac.poly.contains(learnedDays, _cat))) {
            let _dw = _jac.poly.getitem(learnedDays, _cat);
            let _dk = String((_day + 1));
            if ((_dw && _jac.poly.contains(_dw, _dk))) {
              let _dval = _jac.poly.getitem(_dw, _dk);
              if ((_dval > 1.0)) {
                _dval = 1.0;
              }
              if ((_dval < (-1.0))) {
                _dval = (-1.0);
              }
              _dayBonus = _trunc(_pyround((_dval * 5)));
            }
          }
          let _wEnergyEff = _wEnergy;
          let _wPeakEff = _wPeak;
          let _energyV2Penalty = 0;
          if (_energyV2On) {
            _wEnergyEff = 0;
            if ((_btFocus > 0)) {
              let _lvlSum = 0;
              for (const _ls of _rangeList(_slotsNeeded)) {
                _lvlSum = (_lvlSum + energyLevelAt(_eCurve, (_slot + _ls)));
              }
              let _meanLvl = _floordiv(_lvlSum, ((_slotsNeeded > 0) ? _slotsNeeded : 1));
              let _above = (_meanLvl - ENERGY_BASELINE());
              if ((_above < 0)) {
                _above = 0;
              }
              let _effAbove = _above;
              if ((_effAbove > _plateauAbove)) {
                _effAbove = _plateauAbove;
              }
              _peakNorm = _floordiv(((_effAbove * 100) * _btFocus), (_plateauAbove * 10));
              if ((_peakNorm > 100)) {
                _peakNorm = 100;
              }
              let _defSum = 0;
              for (const _ds of _rangeList(_slotsNeeded)) {
                _defSum = (_defSum + energyDeficitAt(_eCurve, (_slot + _ds)));
              }
              let _meanDef = _floordiv(_defSum, ((_slotsNeeded > 0) ? _slotsNeeded : 1));
              _energyV2Penalty = _floordiv(((_W_ENERGY_NUM * _btFocus) * _meanDef), _W_ENERGY_DEN);
            } else {
              _peakNorm = 0;
            }
          }
          let _sleepEdgePenalty = 0;
          if ((_sleepEdgeWeight > 0)) {
            let _chunkEndSlot = ((_slot + _slotsNeeded) - 1);
            if ((_chunkEndSlot < _slot)) {
              _chunkEndSlot = _slot;
            }
            let _earlyDepth = 0;
            if ((_slot < _SLEEP_EARLY_EDGE)) {
              _earlyDepth = (_SLEEP_EARLY_EDGE - _slot);
            }
            let _lateDepth = 0;
            if ((_chunkEndSlot >= _SLEEP_LATE_EDGE)) {
              _lateDepth = ((_chunkEndSlot - _SLEEP_LATE_EDGE) + 1);
            }
            let _edgeDepth = (_earlyDepth + _lateDepth);
            _sleepEdgePenalty = ((_sleepEdgeWeight * 2) * _edgeDepth);
          }
          let _score = ((((_floordiv((((((((((_wEnergyEff * _energyNorm) + (_wPeakEff * _peakNorm)) + (_wDeadline * _deadlineNorm)) + (_wBalance * _balanceNorm)) + (_wSpread * _spreadNorm)) + (_wFlow * _flowNorm)) + (_wRank * _rankNorm)) + (_wTierEnergy * _tierEnergyNorm)) - (_wDayLoad * _dayLoadNorm)), 100) + _affBonus) + _dayBonus) - _energyV2Penalty) - _sleepEdgePenalty);
          let _beatsBest = ((_deadlineRank > _bestDeadlineRank) || ((_deadlineRank === _bestDeadlineRank) && (_score > _bestScore)));
          if (_beatsBest) {
            _secondBestScore = _bestScore;
            _secondBestDay = _bestDay;
            _secondBestSlot = _bestSlot;
            _secondBestDeadlineRank = _bestDeadlineRank;
            _bestScore = _score;
            _bestDeadlineRank = _deadlineRank;
            _bestDay = _day;
            _bestSlot = _slot;
          } else {
            let _beatsSecond = ((_deadlineRank > _secondBestDeadlineRank) || ((_deadlineRank === _secondBestDeadlineRank) && (_score > _secondBestScore)));
            if (_beatsSecond) {
              _secondBestScore = _score;
              _secondBestDeadlineRank = _deadlineRank;
              _secondBestDay = _day;
              _secondBestSlot = _slot;
            }
          }
        }
      }
      _blockedDays = _chunkBlockedDays;
      _capBlockedDays = _chunkCapBlockedDays;
      _countBlockedDays = _chunkCountBlockedDays;
      _deadlineBlockedDays = _chunkDeadlineBlockedDays;
      _depBlockedDays = _chunkDepBlockedDays;
      _windowBlockedDays = _chunkWindowBlockedDays;
      _candidateSlots = _chunkCandidateSlots;
      if ((_bestDay < 0)) {
        break;
      }
      let _pidx = (_placements.length + 1);
      for (const _s of _rangeList(_slotsNeeded)) {
        _grid.at(_bestDay)[(_bestSlot + _s)] = _pidx;
        _gridCat.at(_bestDay)[(_bestSlot + _s)] = _cat;
      }
      if (!_countedDays.includes(_bestDay)) {
        _countedDays = _countedDays.concat([_bestDay]);
        let _dkBest = String(_bestDay);
        if (!(_dkBest in _dayCatCount)) {
          _dayCatCount[_dkBest] = {};
        }
        if (!_jac.poly.contains(_dayCatCount[_dkBest], _cat)) {
          _dayCatCount[_dkBest][_cat] = 0;
        }
        _dayCatCount[_dkBest][_cat] = (_jac.poly.getitem(_dayCatCount[_dkBest], _cat) + 1);
      }
      let _sh = (7 + _floordiv(_bestSlot, 4));
      let _smV = (_jac.int.mod(_bestSlot, 4) * 15);
      let _ehs = (_bestSlot + _slotsNeeded);
      let _eh = (7 + _floordiv(_ehs, 4));
      let _emV = (_jac.int.mod(_ehs, 4) * 15);
      let _sStr = ((_pad2(_sh) + ":") + _pad2(_smV));
      let _eStr = ((_pad2(_eh) + ":") + _pad2(_emV));
      let _conf = _floordiv((_bestScore * 100), 90);
      if ((_conf > 100)) {
        _conf = 100;
      }
      if ((_conf < 1)) {
        _conf = 1;
      }
      let _rbEi = _floordiv(_bestSlot, 4);
      if ((_rbEi >= 16)) {
        _rbEi = 15;
      }
      let _rbPeak = 0;
      if (_energyV2On) {
        if ((_btFocus > 0)) {
          let _rbPkSum = 0;
          for (const _s of _rangeList(_slotsNeeded)) {
            _rbPkSum = (_rbPkSum + energyLevelAt(_eCurve, (_bestSlot + _s)));
          }
          let _rbPkMean = _floordiv(_rbPkSum, ((_slotsNeeded > 0) ? _slotsNeeded : 1));
          if ((_rbPkMean >= (_peakMax - 8))) {
            _rbPeak = _floordiv((_bestTask.priority * 25), 10);
          }
        }
      } else if (((_bestSlot >= _peakS) && (_bestSlot < _peakE))) {
        _rbPeak = _floordiv((_bestTask.priority * 25), 10);
      }
      let _rbDeadline = 0;
      let _rbDl = (_bestTask.deadline ? _bestTask.deadline : "");
      let _rbDaysLeft = (-1);
      if (_rbDl) {
        let _rbCandidateDay = _candidateDate(_today, (_bestDay + 1));
        _rbDaysLeft = _deadlineDaysRemaining(_rbDl, _rbCandidateDay);
        if ((_rbDaysLeft < 0)) {
          _rbDaysLeft = 14;
        }
        _rbDeadline = _floordiv(2000, ((_rbDaysLeft * 100) + 100));
      }
      let _rbDayMins = 0;
      for (const _s of _rangeList(SLOTS)) {
        if ((_jac.poly.getitem(_grid.at(_bestDay), _s) > 0)) {
          _rbDayMins = (_rbDayMins + 15);
        }
      }
      let _rbSpreadDen = ((_dailyMaxMins.at(_bestDay) > 1) ? _dailyMaxMins.at(_bestDay) : 1);
      let _rbSpread = (10 - _floordiv((_rbDayMins * 10), _rbSpreadDen));
      let _rbFlow = 0;
      if ((_bestSlot > 0)) {
        let _rbPrc = _jac.poly.getitem(_gridCat.at(_bestDay), (_bestSlot - 1));
        if ((_rbPrc && (_rbPrc !== ""))) {
          let _rbPcw = ((_rbPrc in _cog) ? _cog[_rbPrc] : 50);
          let _rbCcw = ((_cat in _cog) ? _cog[_cat] : 50);
          if ((_rbPcw > 60)) {
            if (((afterDeepWork === "more") && (_rbCcw > 60))) {
              _rbFlow = 5;
            } else if (((afterDeepWork === "break") && (_rbCcw <= 60))) {
              _rbFlow = 5;
            } else if (((afterDeepWork === "more") && (_rbCcw <= 60))) {
              _rbFlow = (-3);
            } else if (((afterDeepWork === "break") && (_rbCcw > 60))) {
              _rbFlow = (-3);
            }
          }
        }
      }
      let _rbHour = (7 + _floordiv(_bestSlot, 4));
      let _rbHourLabel = ((_rbHour < 12) ? (String(_rbHour) + "am") : ((_rbHour > 12) ? (String((_rbHour - 12)) + "pm") : "12pm"));
      let _rbEnergyPct = ((_rbEi < 16) ? _energy.at(_rbEi) : 10);
      if (_energyV2On) {
        let _rbLvlSum = 0;
        for (const _s of _rangeList(_slotsNeeded)) {
          _rbLvlSum = (_rbLvlSum + energyLevelAt(_eCurve, (_bestSlot + _s)));
        }
        _rbEnergyPct = _floordiv(_rbLvlSum, ((_slotsNeeded > 0) ? _slotsNeeded : 1));
      }
      let _reasons = [];
      if ((_rbEnergyPct >= 70)) {
        _reasons = _reasons.concat([((((("Energy: High energy at " + _rbHourLabel) + " (") + String(_rbEnergyPct)) + "%) \u2014 a strong slot for ") + _cat)]);
      } else if ((_rbEnergyPct >= 40)) {
        _reasons = _reasons.concat([((((("Energy: Moderate energy at " + _rbHourLabel) + " (") + String(_rbEnergyPct)) + "%) \u2014 a solid slot for ") + _cat)]);
      } else {
        _reasons = _reasons.concat([(((("Energy: Lower energy at " + _rbHourLabel) + " (") + String(_rbEnergyPct)) + "%) \u2014 better suited to lighter tasks")]);
      }
      if ((_rbPeak > 0)) {
        _reasons = _reasons.concat(["Focus peak: Placed during your peak focus window"]);
      } else {
        _reasons = _reasons.concat(["Focus peak: Outside your peak hours \u2014 lower-priority or lighter work fits here"]);
      }
      if (_rbDl) {
        if ((_rbDaysLeft <= 1)) {
          _reasons = _reasons.concat(["Deadline: Due very soon (within 1 day) \u2014 urgent"]);
        } else if ((_rbDaysLeft <= 3)) {
          _reasons = _reasons.concat([(("Deadline: Due in ~" + String(_rbDaysLeft)) + " days \u2014 approaching")]);
        } else if ((_rbDaysLeft <= 7)) {
          _reasons = _reasons.concat([(("Deadline: Due in ~" + String(_rbDaysLeft)) + " days \u2014 comfortable")]);
        } else {
          _reasons = _reasons.concat([(("Deadline: Due in ~" + String(_rbDaysLeft)) + " days \u2014 not pressing")]);
        }
      } else {
        _reasons = _reasons.concat(["Deadline: No deadline set \u2014 scheduled by priority and fit"]);
      }
      if ((_rbSpread > 6)) {
        _reasons = _reasons.concat(["Day balance: Light day \u2014 room to add this task"]);
      } else {
        _reasons = _reasons.concat(["Day balance: Moderate load already \u2014 fits but the day is filling up"]);
      }
      if ((_rbFlow > 0)) {
        _reasons = _reasons.concat(["Flow: Follows similar deep work \u2014 matches your continuation preference"]);
      } else if ((_rbFlow < 0)) {
        _reasons = _reasons.concat(["Flow: Category switch after deep work \u2014 against your preferred flow"]);
      }
      if ((_chunkList.length > 1)) {
        _reasons = _reasons.concat([(("Session plan: Split into " + String(_chunkList.length)) + " shorter sessions to avoid one long block")]);
      }
      if ((_prefTimeStr && (_prefSlotStart >= 0))) {
        _reasons = _reasons.concat([(("Time preference: Placed near preferred time (" + String(_prefTimeStr)) + ")")]);
      }
      if ((_prefDateStr && (_prefDay > 0))) {
        _reasons = _reasons.concat([(("Date preference: Scheduled on preferred date (" + String(_prefDateStr)) + ")")]);
      }
      let _placementId = ("p-" + _bestTask.id);
      if ((_chunkList.length > 1)) {
        _placementId = ((_placementId + "-") + String((_chunkIdx + 1)));
      }
      _placements = _placements.concat([{"id": _placementId, "task_id": _bestTask.id, "task_title": _bestTask.title, "day": (_bestDay + 1), "start_time": _sStr, "end_time": _eStr, "duration_mins": _chunkMins, "category": _cat, "deadline": (_bestTask.deadline ? _bestTask.deadline : ""), "scheduled_date": (_prefDateStr ? _prefDateStr : ""), "confidence": _conf, "score": _bestScore, "runner_up_slot": ((_secondBestSlot >= 0) ? (420 + (_secondBestSlot * 15)) : null), "runner_up_score": ((_secondBestScore >= 0) ? _secondBestScore : null), "runner_up_day": ((_secondBestDay >= 0) ? (_secondBestDay + 1) : null), "chunk_index": (_chunkIdx + 1), "chunk_total": _chunkList.length, "is_committed": false, "is_fixed": false, "reasons": _reasons}]);
      _remainingMins = (_remainingMins - _chunkMins);
    }
    if ((_remainingMins <= 0)) {
      _placedIds = _placedIds.concat([_bestTask.id]);
    } else {
      let _reasonParts = [];
      if (_jac.builtin.bool(_jac.poly.and_(_blockedDays, () => (_blockedDays.length > 0)))) {
        let _blockedNames = _blockedDays.map(d => {
          return _dayName(d);
        });
        _reasonParts = _reasonParts.concat([("Blocked by category-day rules on " + _jac.poly.call(_blockedNames, "join", ", "))]);
      }
      if (_jac.builtin.bool(_jac.poly.and_(_capBlockedDays, () => (_capBlockedDays.length > 0)))) {
        let _capNames = _capBlockedDays.map(d => {
          return _dayName(d);
        });
        _reasonParts = _reasonParts.concat([("Daily hour cap exceeded on " + _jac.poly.call(_capNames, "join", ", "))]);
      }
      if (_jac.builtin.bool(_jac.poly.and_(_countBlockedDays, () => (_countBlockedDays.length > 0)))) {
        let _countNames = _countBlockedDays.map(d => {
          return _dayName(d);
        });
        _reasonParts = _reasonParts.concat([((("Daily " + String((((_bestTask.category !== undefined) && (_bestTask.category !== null)) ? _bestTask.category : ""))) + " count cap reached on ") + _jac.poly.call(_countNames, "join", ", "))]);
      }
      if (_jac.builtin.bool(_jac.poly.and_(((_candidateSlots === 0) && _depBlockedDays), () => (_depBlockedDays.length > 0)))) {
        let _depNames = _depBlockedDays.map(d => {
          return _dayName(d);
        });
        _reasonParts = _reasonParts.concat([("Dependency: must come after its prerequisite \u2014 no slot left after it on " + _jac.poly.call(_depNames, "join", ", "))]);
      }
      if (_jac.builtin.bool(_jac.poly.and_(((_candidateSlots === 0) && _windowBlockedDays), () => (_windowBlockedDays.length > 0)))) {
        let _winNames = _windowBlockedDays.map(d => {
          return _dayName(d);
        });
        _reasonParts = _reasonParts.concat([("Time window: no open slot inside the allowed hours on " + _jac.poly.call(_winNames, "join", ", "))]);
      }
      let _isDeadlineConflict = false;
      let _effDeadlineStr = "";
      let _ovDl = (_btDeadline ? _btDeadline : "");
      if (_jac.builtin.bool(_jac.poly.and_(_jac.poly.and_(((_candidateSlots === 0) && _deadlineBlockedDays), () => (_deadlineBlockedDays.length > 0)), () => _ovDl))) {
        let _ovDd = _fromIso(_ovDl);
        if ((_ovDd !== null)) {
          _isDeadlineConflict = true;
          _effDeadlineStr = _isoStr(_addDays(_ovDd, (-1)));
          _reasonParts = _reasonParts.concat([(((("Deadline: can't fit before its deadline (must finish by " + _effDeadlineStr) + ", the day before ") + _ovDl) + ")")]);
        }
      }
      if ((_candidateSlots === 0)) {
        if (_jac.builtin.bool(_jac.poly.and_(_taskGaps, () => (_taskGaps.length > 0)))) {
          _reasonParts = _reasonParts.concat([(("Minimum-gap rule: no slot keeps the required " + String(_maxGapSpan)) + "-min spacing from already-placed items")]);
        }
        if (!_isDeadlineConflict) {
          _reasonParts = _reasonParts.concat(["No contiguous slot fits current fixed events/buffer"]);
        }
      }
      if ((_remainingMins < _adjMins)) {
        _reasonParts = _reasonParts.concat(["Partially scheduled already \u2014 remaining time still needs another open session"]);
      }
      if (!_reasonParts.length) {
        _reasonParts = _reasonParts.concat(["Could not satisfy constraints for a valid placement"]);
      }
      let _shortReason = _reasonParts[0];
      _overflowList = _overflowList.concat([{"id": _bestTask.id, "title": _bestTask.title, "duration_mins": _bestTask.duration_mins, "priority": _bestTask.priority, "category": (((_bestTask.category !== undefined) && (_bestTask.category !== null)) ? _bestTask.category : "other"), "effective_duration_mins": _adjMins, "scheduled_duration_mins": (_adjMins - _remainingMins), "remaining_duration_mins": _remainingMins, "multiplier_pct": ((_cat in _mults) ? _mults[_cat] : 100), "required_contiguous_mins": _failedChunkMins, "blocked_days": _blockedDays, "cap_blocked_days": _capBlockedDays, "count_blocked_days": _countBlockedDays, "deadline_blocked_days": _deadlineBlockedDays, "dep_blocked_days": _depBlockedDays, "window_blocked_days": _windowBlockedDays, "is_deadline_conflict": _isDeadlineConflict, "deadline": _ovDl, "effective_deadline": _effDeadlineStr, "candidate_slots": _candidateSlots, "reason_short": _shortReason, "reason_details": _reasonParts}]);
      _placedIds = _placedIds.concat([_bestTask.id]);
    }
  }
  if (_energyV2On) {
    let _ENERGY_K = 8;
    let _taskFocusById = {};
    let _taskWinById = {};
    let _taskDepsById = {};
    for (const _tk of tasks) {
      let _tkId = String(_tk.id);
      _taskFocusById[_tkId] = focusForTask(_tk);
      let _tkDeps = (((_tk.depends_on !== undefined) && (_tk.depends_on !== null)) ? _tk.depends_on : []);
      _taskDepsById[_tkId] = (_tkDeps ? _tkDeps : []);
      let _tkWin = (((_tk.time_window !== undefined) && (_tk.time_window !== null)) ? _tk.time_window : null);
      _taskWinById[_tkId] = null;
      if ((_tkWin && (_tkWin.length >= 2))) {
        let _tkWs = _tkWin[0];
        let _tkWe = _tkWin[1];
        if ((((((_tkWs !== undefined) && (_tkWs !== null)) && (_tkWe !== undefined)) && (_tkWe !== null)) && (_tkWe > _tkWs))) {
          let _tkWsSlot = _floordiv(((_tkWs - 420) + 14), 15);
          if ((_tkWsSlot < 0)) {
            _tkWsSlot = 0;
          }
          let _tkWeSlot = _floordiv((_tkWe - 420), 15);
          if ((_tkWeSlot > SLOTS)) {
            _tkWeSlot = SLOTS;
          }
          if ((_tkWeSlot < 0)) {
            _tkWeSlot = 0;
          }
          if ((_tkWeSlot > _tkWsSlot)) {
            _taskWinById[_tkId] = [_tkWsSlot, _tkWeSlot];
          }
        }
      }
    }
    let _relocRounds = 0;
    while ((_relocRounds < _ENERGY_K)) {
      _relocRounds = (_relocRounds + 1);
      let _movedAny = false;
      let _ordered = _jac.builtin.sorted(_placements, _placementStableSortKey);
      for (const _pl of _ordered) {
        if (_pl.is_fixed) {
          continue;
        }
        let _plFocus = ((String(_pl.task_id) in _taskFocusById) ? _taskFocusById[String(_pl.task_id)] : 0);
        if ((_plFocus <= 0)) {
          continue;
        }
        let _plDayIdx = (_pl.day - 1);
        if (((_plDayIdx < 0) || (_plDayIdx > 6))) {
          continue;
        }
        let _curStart = _parseHhmm(_pl.start_time);
        let _curEnd = _parseHhmm(_pl.end_time);
        if (((_curStart < 0) || (_curEnd < 0))) {
          continue;
        }
        let _curSlot = _floordiv((_curStart - 420), 15);
        let _need = _floordiv((_curEnd - _curStart), 15);
        if (((_need <= 0) || (_curSlot < 0))) {
          continue;
        }
        let _curDefSum = 0;
        for (const _cs of _rangeList(_need)) {
          _curDefSum = (_curDefSum + energyDeficitAt(_eCurve, (_curSlot + _cs)));
        }
        let _curMeanDef = _floordiv(_curDefSum, ((_need > 0) ? _need : 1));
        let _curPen = _floordiv(((_W_ENERGY_NUM * _plFocus) * _curMeanDef), _W_ENERGY_DEN);
        if ((_curPen <= 0)) {
          continue;
        }
        for (const _cs of _rangeList(_need)) {
          let _ci = (_curSlot + _cs);
          if (((_ci >= 0) && (_ci < SLOTS))) {
            _grid.at(_plDayIdx)[_ci] = 0;
            _gridCat.at(_plDayIdx)[_ci] = "";
          }
        }
        let _relocDepFloor = (-1);
        let _plDeps = ((String(_pl.task_id) in _taskDepsById) ? _taskDepsById[String(_pl.task_id)] : []);
        if ((_plDeps && (_plDeps.length > 0))) {
          for (const _rdp of _placements) {
            if (((_rdp.day - 1) !== _plDayIdx)) {
              continue;
            }
            let _rdpMatch = false;
            for (const _depId of _plDeps) {
              if ((String(_rdp.task_id) === String(_depId))) {
                _rdpMatch = true;
              }
            }
            if (!_rdpMatch) {
              continue;
            }
            let _rdpEndM = _parseHhmm(_rdp.end_time);
            if ((_rdpEndM < 0)) {
              continue;
            }
            let _rdpEndSlot = _floordiv((_rdpEndM - 420), 15);
            if ((_rdpEndSlot > _relocDepFloor)) {
              _relocDepFloor = _rdpEndSlot;
            }
          }
        }
        let _relocWin = ((String(_pl.task_id) in _taskWinById) ? _taskWinById[String(_pl.task_id)] : null);
        let _bestPen = _curPen;
        let _bestNewSlot = (-1);
        for (const _cand of _rangeList(((SLOTS - _need) + 1))) {
          if (((skipPastDays && ((_plDayIdx + 1) === _todayWeekday)) && (_cand < _earliestTodaySlot))) {
            continue;
          }
          if ((_relocWin !== null)) {
            if (((_cand < _relocWin[0]) || ((_cand + _need) > _relocWin[1]))) {
              continue;
            }
          }
          if (((_relocDepFloor >= 0) && (_cand < _relocDepFloor))) {
            continue;
          }
          let _free = true;
          for (const _cs of _rangeList(_need)) {
            if ((_jac.poly.getitem(_grid.at(_plDayIdx), (_cand + _cs)) !== 0)) {
              _free = false;
              break;
            }
          }
          if (!_free) {
            continue;
          }
          let _candDefSum = 0;
          for (const _cs of _rangeList(_need)) {
            _candDefSum = (_candDefSum + energyDeficitAt(_eCurve, (_cand + _cs)));
          }
          let _candMeanDef = _floordiv(_candDefSum, ((_need > 0) ? _need : 1));
          let _candPen = _floordiv(((_W_ENERGY_NUM * _plFocus) * _candMeanDef), _W_ENERGY_DEN);
          if ((_candPen < _bestPen)) {
            _bestPen = _candPen;
            _bestNewSlot = _cand;
          }
        }
        let _placeSlot = _curSlot;
        if (((_bestNewSlot >= 0) && (_bestNewSlot !== _curSlot))) {
          _placeSlot = _bestNewSlot;
          _movedAny = true;
        }
        let _plCat = (((_pl.category !== undefined) && (_pl.category !== null)) ? _pl.category : "other");
        for (const _cs of _rangeList(_need)) {
          let _ci = (_placeSlot + _cs);
          if (((_ci >= 0) && (_ci < SLOTS))) {
            _grid.at(_plDayIdx)[_ci] = 1;
            _gridCat.at(_plDayIdx)[_ci] = _plCat;
          }
        }
        if ((_placeSlot !== _curSlot)) {
          let _nSh = (7 + _floordiv(_placeSlot, 4));
          let _nSm = (_jac.int.mod(_placeSlot, 4) * 15);
          let _nEslot = (_placeSlot + _need);
          let _nEh = (7 + _floordiv(_nEslot, 4));
          let _nEm = ((_nEslot % 4) * 15);
          _pl.start_time = ((_pad2(_nSh) + ":") + _pad2(_nSm));
          _pl.end_time = ((_pad2(_nEh) + ":") + _pad2(_nEm));
          _pl.runner_up_slot = (420 + (_curSlot * 15));
        }
      }
      if (!_movedAny) {
        break;
      }
    }
    let _swapTelemetryOn = false;
    try {
      if (window.__skoochSwapTelemetry) {
        _swapTelemetryOn = true;
      }
    } catch (__jac_e) {
      if (true) {
        let _e = __jac_e;
      } else {
        throw __jac_e;
      }
    }
    if (_swapTelemetryOn) {
      window.__skoochSwapTelemetry = {"attempted": 0, "vetoFlow": 0, "vetoGap": 0, "accepted": 0};
    }
    let _swapRounds = 0;
    while ((_swapRounds < _ENERGY_K)) {
      _swapRounds = (_swapRounds + 1);
      let _swappedAny = false;
      let _orderedS = _jac.builtin.sorted(_placements, _placementDayStartSortKey);
      for (const _ai of _rangeList(_orderedS.length)) {
        let _A = _jac.poly.getitem(_orderedS, _ai);
        if (_A.is_fixed) {
          continue;
        }
        let _aFocus = ((String(_A.task_id) in _taskFocusById) ? _taskFocusById[String(_A.task_id)] : 0);
        let _aStart = _parseHhmm(_A.start_time);
        let _aEnd = _parseHhmm(_A.end_time);
        if (((_aStart < 0) || (_aEnd < 0))) {
          continue;
        }
        let _aSlot = _floordiv((_aStart - 420), 15);
        let _aNeed = _floordiv((_aEnd - _aStart), 15);
        for (const _bi of _rangeList(_orderedS.length)) {
          if ((_bi <= _ai)) {
            continue;
          }
          let _B = _jac.poly.getitem(_orderedS, _bi);
          if (_B.is_fixed) {
            continue;
          }
          if ((_B.day !== _A.day)) {
            continue;
          }
          if ((_A.task_id === _B.task_id)) {
            continue;
          }
          let _bFocus = ((String(_B.task_id) in _taskFocusById) ? _taskFocusById[String(_B.task_id)] : 0);
          let _bStart = _parseHhmm(_B.start_time);
          let _bEnd = _parseHhmm(_B.end_time);
          if (((_bStart < 0) || (_bEnd < 0))) {
            continue;
          }
          let _bSlot = _floordiv((_bStart - 420), 15);
          let _bNeed = _floordiv((_bEnd - _bStart), 15);
          if ((_aNeed !== _bNeed)) {
            continue;
          }
          if (((_aFocus <= 0) || (_bFocus <= 0))) {
            continue;
          }
          let _aDefSum = 0;
          let _bDefSum = 0;
          for (const _cs of _rangeList(_aNeed)) {
            _aDefSum = (_aDefSum + energyDeficitAt(_eCurve, (_aSlot + _cs)));
          }
          for (const _cs of _rangeList(_bNeed)) {
            _bDefSum = (_bDefSum + energyDeficitAt(_eCurve, (_bSlot + _cs)));
          }
          let _aMean = _floordiv(_aDefSum, ((_aNeed > 0) ? _aNeed : 1));
          let _bMean = _floordiv(_bDefSum, ((_bNeed > 0) ? _bNeed : 1));
          let _aEffAbove = (energyLevelAt(_eCurve, _aSlot) - ENERGY_BASELINE());
          let _bEffAbove = (energyLevelAt(_eCurve, _bSlot) - ENERGY_BASELINE());
          if ((_aNeed > 1)) {
            let _aLvlSum = 0;
            for (const _cs of _rangeList(_aNeed)) {
              _aLvlSum = (_aLvlSum + energyLevelAt(_eCurve, (_aSlot + _cs)));
            }
            _aEffAbove = (_floordiv(_aLvlSum, _aNeed) - ENERGY_BASELINE());
          }
          if ((_bNeed > 1)) {
            let _bLvlSum = 0;
            for (const _cs of _rangeList(_bNeed)) {
              _bLvlSum = (_bLvlSum + energyLevelAt(_eCurve, (_bSlot + _cs)));
            }
            _bEffAbove = (_floordiv(_bLvlSum, _bNeed) - ENERGY_BASELINE());
          }
          if ((_aEffAbove < 0)) {
            _aEffAbove = 0;
          }
          if ((_bEffAbove < 0)) {
            _bEffAbove = 0;
          }
          if ((_aEffAbove > _plateauAbove)) {
            _aEffAbove = _plateauAbove;
          }
          if ((_bEffAbove > _plateauAbove)) {
            _bEffAbove = _plateauAbove;
          }
          let _aMis = (_aFocus * (_plateauAbove - _aEffAbove));
          let _bMis = (_bFocus * (_plateauAbove - _bEffAbove));
          let _curSum = (((_floordiv(((_W_ENERGY_NUM * _aFocus) * _aMean), _W_ENERGY_DEN) + _floordiv(((_W_ENERGY_NUM * _bFocus) * _bMean), _W_ENERGY_DEN)) + _aMis) + _bMis);
          let _aMisSw = (_aFocus * (_plateauAbove - _bEffAbove));
          let _bMisSw = (_bFocus * (_plateauAbove - _aEffAbove));
          let _swapSum = (((_floordiv(((_W_ENERGY_NUM * _aFocus) * _bMean), _W_ENERGY_DEN) + _floordiv(((_W_ENERGY_NUM * _bFocus) * _aMean), _W_ENERGY_DEN)) + _aMisSw) + _bMisSw);
          let _aCat = (((_A.category !== undefined) && (_A.category !== null)) ? _A.category : "other");
          let _bCat = (((_B.category !== undefined) && (_B.category !== null)) ? _B.category : "other");
          let _aOccEnd = (_aSlot + _aNeed);
          let _bOccEnd = (_bSlot + _bNeed);
          let _neCur = (_swapFlowAffScore(_gridCat, (_A.day - 1), _aSlot, _aNeed, _aCat, _cog, learnedAffinity, afterDeepWork, _wFlow, _aSlot, _aOccEnd, "", 0, 0) + _swapFlowAffScore(_gridCat, (_B.day - 1), _bSlot, _bNeed, _bCat, _cog, learnedAffinity, afterDeepWork, _wFlow, _bSlot, _bOccEnd, "", 0, 0));
          let _neSw = (_swapFlowAffScore(_gridCat, (_A.day - 1), _bSlot, _aNeed, _aCat, _cog, learnedAffinity, afterDeepWork, _wFlow, _aSlot, _aOccEnd, _bCat, _aSlot, _aOccEnd) + _swapFlowAffScore(_gridCat, (_B.day - 1), _aSlot, _bNeed, _bCat, _cog, learnedAffinity, afterDeepWork, _wFlow, _bSlot, _bOccEnd, _aCat, _bSlot, _bOccEnd));
          let _gapFeasible = true;
          if (((((_swapSum < _curSum) && (_neSw >= _neCur)) && gapConstraints) && (gapConstraints.length > 0))) {
            let _aNewStartM = (420 + (_bSlot * 15));
            let _aNewEndM = (420 + ((_bSlot + _aNeed) * 15));
            let _bNewStartM = (420 + (_aSlot * 15));
            let _bNewEndM = (420 + ((_aSlot + _bNeed) * 15));
            let _btTitleA = (((_A.task_title !== undefined) && (_A.task_title !== null)) ? _A.task_title : "");
            let _btTitleB = (((_B.task_title !== undefined) && (_B.task_title !== null)) ? _B.task_title : "");
            if (!_swapGapOk(_A.task_id, _btTitleA, _aCat, _aNewStartM, _aNewEndM, _A.day, _B.task_id, gapConstraints, _placements)) {
              _gapFeasible = false;
            } else if (!_swapGapOk(_B.task_id, _btTitleB, _bCat, _bNewStartM, _bNewEndM, _B.day, _A.task_id, gapConstraints, _placements)) {
              _gapFeasible = false;
            }
          }
          let _t2Feasible = true;
          if (((_swapSum < _curSum) && (_neSw >= _neCur))) {
            let _aWin = ((String(_A.task_id) in _taskWinById) ? _taskWinById[String(_A.task_id)] : null);
            let _bWin = ((String(_B.task_id) in _taskWinById) ? _taskWinById[String(_B.task_id)] : null);
            if (((_aWin !== null) && ((_bSlot < _aWin[0]) || ((_bSlot + _aNeed) > _aWin[1])))) {
              _t2Feasible = false;
            }
            if (((_bWin !== null) && ((_aSlot < _bWin[0]) || ((_aSlot + _bNeed) > _bWin[1])))) {
              _t2Feasible = false;
            }
            if (_t2Feasible) {
              let _aDepFloor = _sameDayDepFloor(_A.task_id, _taskDepsById, _placements, _A.day);
              let _bDepFloor = _sameDayDepFloor(_B.task_id, _taskDepsById, _placements, _A.day);
              if (((_aDepFloor >= 0) && (_bSlot < _aDepFloor))) {
                _t2Feasible = false;
              }
              if (((_bDepFloor >= 0) && (_aSlot < _bDepFloor))) {
                _t2Feasible = false;
              }
            }
          }
          if ((_swapTelemetryOn && (_swapSum < _curSum))) {
            window.__skoochSwapTelemetry["attempted"] = (window.__skoochSwapTelemetry["attempted"] + 1);
            if ((_neSw < _neCur)) {
              window.__skoochSwapTelemetry["vetoFlow"] = (window.__skoochSwapTelemetry["vetoFlow"] + 1);
            } else if (!_gapFeasible) {
              window.__skoochSwapTelemetry["vetoGap"] = (window.__skoochSwapTelemetry["vetoGap"] + 1);
            } else if (!_t2Feasible) {
              window.__skoochSwapTelemetry["vetoGap"] = (window.__skoochSwapTelemetry["vetoGap"] + 1);
            } else {
              window.__skoochSwapTelemetry["accepted"] = (window.__skoochSwapTelemetry["accepted"] + 1);
            }
          }
          if (((((_swapSum < _curSum) && (_neSw >= _neCur)) && _gapFeasible) && _t2Feasible)) {
            let _aSh = (7 + _floordiv(_bSlot, 4));
            let _aSm = (_jac.int.mod(_bSlot, 4) * 15);
            let _aEslot = (_bSlot + _aNeed);
            let _aEh2 = (7 + _floordiv(_aEslot, 4));
            let _aEm2 = (_jac.int.mod(_aEslot, 4) * 15);
            let _bSh = (7 + _floordiv(_aSlot, 4));
            let _bSm = (_jac.int.mod(_aSlot, 4) * 15);
            let _bEslot = (_aSlot + _bNeed);
            let _bEh2 = (7 + _floordiv(_bEslot, 4));
            let _bEm2 = (_jac.int.mod(_bEslot, 4) * 15);
            _A.start_time = ((_pad2(_aSh) + ":") + _pad2(_aSm));
            _A.end_time = ((_pad2(_aEh2) + ":") + _pad2(_aEm2));
            _B.start_time = ((_pad2(_bSh) + ":") + _pad2(_bSm));
            _B.end_time = ((_pad2(_bEh2) + ":") + _pad2(_bEm2));
            let _swapDayIdx = (_A.day - 1);
            if (((_swapDayIdx >= 0) && (_swapDayIdx <= 6))) {
              for (const _cs of _rangeList(_aNeed)) {
                let _ca = (_aSlot + _cs);
                let _cb = (_bSlot + _cs);
                if (((_ca >= 0) && (_ca < SLOTS))) {
                  _gridCat.at(_swapDayIdx)[_ca] = _bCat;
                }
                if (((_cb >= 0) && (_cb < SLOTS))) {
                  _gridCat.at(_swapDayIdx)[_cb] = _aCat;
                }
              }
            }
            _swappedAny = true;
            break;
          }
        }
        if (_swappedAny) {
          break;
        }
      }
      if (!_swappedAny) {
        break;
      }
    }
  }
  let _suggestions = [];
  if (_overflowList.length) {
    let _deficit = 0;
    for (const _ot of _overflowList) {
      _deficit = (_deficit + (((_ot.remaining_duration_mins !== undefined) && (_ot.remaining_duration_mins !== null)) ? _ot.remaining_duration_mins : _ot.duration_mins));
    }
    _suggestions = _suggestions.concat([{"text": "Increase early-day scheduling capacity by 1h/day: frees ~420 min/week", "freed": 420, "action": "extend_earlier"}]);
    _suggestions = _suggestions.concat([{"text": "Increase late-day scheduling capacity by 1h/day: frees ~420 min/week", "freed": 420, "action": "extend_later"}]);
    if ((bufferMins > 0)) {
      let _estSw = (((_placements.length - 1) > 0) ? (_placements.length - 1) : 0);
      let _freedB = (_estSw * bufferMins);
      _suggestions = _suggestions.concat([{"text": (("Remove buffer: frees ~" + String(_freedB)) + " min"), "freed": _freedB, "action": "remove_buffer"}]);
    }
    if (_overflowList.length) {
      let _lowT = _overflowList[0];
      for (const _ot of _overflowList) {
        if ((_ot.priority < _lowT.priority)) {
          _lowT = _ot;
        }
      }
      _suggestions = _suggestions.concat([{"text": (((((("Drop " + _lowT.title) + " (P:") + String(_lowT.priority)) + "): frees ") + String(_lowT.duration_mins)) + "m"), "freed": _lowT.duration_mins, "action": "drop_task", "task_id": _lowT.id}]);
    }
  }
  let _placedUnique = 0;
  let _placedSeen = {};
  for (const _p of _placements) {
    let _tidK = String(_p.task_id);
    if (!(_tidK in _placedSeen)) {
      _placedSeen[_tidK] = true;
      _placedUnique = (_placedUnique + 1);
    }
  }
  let _logMsg = (((("Placed " + String(_placedUnique)) + "/") + String(tasks.length)) + " tasks.");
  if (_overflowList.length) {
    let _names = [];
    for (const _ot of _overflowList) {
      _names = _names.concat([_ot.title]);
    }
    _logMsg = (((_logMsg + " Overflow: ") + _jac.poly.call(_names, "join", ", ")) + ".");
  }
  let _sortedPlacements = _jac.builtin.sorted(_placements, _placementFinalSortKey);
  return {"placements": _sortedPlacements, "overflow": _overflowList, "suggestions": _suggestions, "log": _logMsg};
}
function _dictLen(d) {
  if (!d) {
    return 0;
  }
  return _jac.poly.call(Object, "keys", d).length;
}
function _padNum(n) {
  let _s = String(n);
  let _pad = "0000000000";
  if ((_s.length >= 10)) {
    return _s;
  }
  return (_pad.slice(0, (10 - _s.length)) + _s);
}
function runScheduleOptimizerClient(p) {
  if (!p) {
    p = {};
  }
  let _tasks = (p.tasks ? p.tasks : []);
  let _fixedEvents = (p.fixed_events ? p.fixed_events : []);
  let _constraints = (p.constraints ? p.constraints : []);
  let _categoryTiers = (p.category_tiers ? p.category_tiers : []);
  let _catOverrides = (p.cat_overrides ? p.cat_overrides : []);
  let _globalRules = (p.global_rules ? p.global_rules : []);
  let _workStyle = (((p.work_style !== undefined) && (p.work_style !== null)) ? p.work_style : "");
  let _workLifeBalance = (((p.work_life_balance !== undefined) && (p.work_life_balance !== null)) ? p.work_life_balance : 5);
  let _lockPersonalTime = (((p.lock_personal_time !== undefined) && (p.lock_personal_time !== null)) ? p.lock_personal_time : false);
  let _focusPeak = (((p.focus_peak !== undefined) && (p.focus_peak !== null)) ? p.focus_peak : "");
  let _blockLength = (((p.block_length !== undefined) && (p.block_length !== null)) ? p.block_length : 0);
  let _bufferMins = (((p.buffer_mins !== undefined) && (p.buffer_mins !== null)) ? p.buffer_mins : 0);
  let _strategy = (((p.strategy !== undefined) && (p.strategy !== null)) ? p.strategy : "");
  let _afterDeepWork = (((p.after_deep_work !== undefined) && (p.after_deep_work !== null)) ? p.after_deep_work : "");
  let _sleepImportance = (((p.sleep_importance !== undefined) && (p.sleep_importance !== null)) ? p.sleep_importance : 5);
  let _skipPastDays = (((p.skip_past_days !== undefined) && (p.skip_past_days !== null)) ? p.skip_past_days : true);
  let _learnedFocusPeak = (((p.learned_focus_peak !== undefined) && (p.learned_focus_peak !== null)) ? p.learned_focus_peak : "");
  let _focusPeakOverridden = (((p.focus_peak_overridden !== undefined) && (p.focus_peak_overridden !== null)) ? p.focus_peak_overridden : false);
  let _userId = (((p.user_id !== undefined) && (p.user_id !== null)) ? p.user_id : "");
  let _forceSpread = (((p.force_spread !== undefined) && (p.force_spread !== null)) ? p.force_spread : 0);
  let _catMults = buildCategoryMultipliers(_catOverrides);
  let _weights = computeScoringWeights(_workStyle, _workLifeBalance);
  if ((_forceSpread > 0)) {
    _weights["spread"] = _forceSpread;
  }
  let _tScores = computeTierScores(_categoryTiers);
  let _compiledRules = compileProfileRules(_globalRules, _lockPersonalTime);
  let _allConstraints = [];
  let _gapConstraints = [];
  for (const _r of _compiledRules) {
    if (((((_r.type !== undefined) && (_r.type !== null)) ? _r.type : "") === "min_gap")) {
      _gapConstraints = _gapConstraints.concat([_r]);
    } else {
      _allConstraints = _allConstraints.concat([_r]);
    }
  }
  for (const _c of _constraints) {
    if (((((_c.type !== undefined) && (_c.type !== null)) ? _c.type : "") === "min_gap")) {
      _gapConstraints = _gapConstraints.concat([_c]);
    } else {
      _allConstraints = _allConstraints.concat([_c]);
    }
  }
  let _effectivePeak = _effectiveFocusPeak(_focusPeak, _learnedFocusPeak, _focusPeakOverridden);
  let _learnedAff = {};
  let _learnedDays = {};
  let _energyShape = (((p.energy_shape !== undefined) && (p.energy_shape !== null)) ? p.energy_shape : "");
  let _res = generateScheduleClient(_tasks, _fixedEvents, _allConstraints, _effectivePeak, _blockLength, _bufferMins, _strategy, _afterDeepWork, _catMults, _weights, _tScores, _sleepImportance, _skipPastDays, _learnedAff, _learnedDays, _gapConstraints, _energyShape);
  return {"ok": true, "placements": _res.placements, "overflow": _res.overflow, "suggestions": _res.suggestions, "log": _res.log, "learned": null};
}
export {DEADLINE_URGENCY_BASE, TIER_ENERGY_NEUTRAL, _addDays, _candidateDate, _chunkDurations, _clearGapMins, _dayDiff, _dayName, _deadlineDaysRemaining, _dictLen, _effectiveDeadlineDays, _effectiveFocusPeak, _fixedEventSortKey, _floordiv, _fromIso, _gapSideMatches, _isDigits, _isSleepTaskName, _isoStr, _isoWeekday, _pad2, _padNum, _parseHhmm, _placementDayStartSortKey, _placementFinalSortKey, _placementStableSortKey, _pyround, _rangeList, _sameDayDepFloor, _slotToBucket, _swapFlowAffScore, _swapGapOk, _taskSortKey, _todayDate, _trunc, _tupleKey, blendLearnedDurationMults, buildCategoryMultipliers, compileProfileRules, computeScoringWeights, computeTierScores, explicitOverrideCategories, generateScheduleClient, runScheduleOptimizerClient};
//# sourceMappingURL=scheduler_client.js.map
